import { useState, useRef, useEffect, useMemo, useCallback, CSSProperties } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { photoCycleFromExtras } from '../utils/photoSlotCycle'
import { motion, AnimatePresence } from 'motion/react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Moon,
  Scale,
  Camera,
  Dumbbell,
  Clock,
  Sunrise,
  Sun,
  Sunset,
  Utensils,
  Play,
  Pencil,
  RefreshCw,
  CheckCircle,
  Flame,
  GraduationCap,
  Star,
  MessageSquareHeart,
  MessageSquare,
  X,
  Plus,
  BarChart3,
  Ruler,
  Video,
  Target,
} from 'lucide-react'
import { UnreadCommentBadge } from '../components/UnreadCommentBadge'
import { SneakerIcon } from '../components/SneakerIcon'
import { FatSecretIcon } from '../components/FatSecretIcon'
import { FatSecretAttribution } from '../components/FatSecretAttribution'
import { Layout } from '../components/Layout'
import { NotificationsBell } from '../components/NotificationsBell'
import { TuCoinIcon } from '../components/TuCoinIcon'
import { SleepGuideSheet } from '../components/modals/SleepGuideSheet'
import { GlassCard } from '../components/GlassCard'
import { MetricsEditSheet } from '../components/modals/MetricsEditSheet'
import { NutritionGoalSheet } from '../components/modals/NutritionGoalSheet'
import type { MetricsValues } from '../components/modals/MetricsEditSheet'
import { MeasurementsSheet } from '../components/modals/MeasurementsSheet'
import { MeasurementsCongrats } from '../components/modals/MeasurementsCongrats'
import { RewardCongrats } from '../components/modals/RewardCongrats'
import { StreakSuccessModal } from '../components/modals/StreakSuccessModal'
import { PhotoReportSheet } from '../components/modals/PhotoReportSheet'
import { FeedbackSurveySheet } from '../components/modals/FeedbackSurveySheet'
import { NpsSurveySheet } from '../components/modals/NpsSurveySheet'
import { PavelChatSheet } from '../components/modals/PavelChatSheet'
import { SkeletonRow } from '../components/Skeleton'
import { TfaLogo } from '../components/TfaLogo'
import { CommentsInboxSheet } from '../components/modals/CommentsInboxSheet'
import { AddMealSheet } from '../components/modals/AddMealSheet'
import { DayNutritionSheet } from '../components/modals/DayNutritionSheet'
import { FatSecretConnectSheet } from '../components/modals/FatSecretConnectSheet'
import { canOfferFatSecretConnect } from '../lib/fatsecretAccess'
import { ActiveChallengeBlock } from '../components/ActiveChallengeBlock'
import { CuratorCommentThread, type CuratorCommentDto } from '../components/CuratorCommentThread'
import { useAuth } from '../hooks/useAuth'
import { useCuratorHidden } from '../hooks/useCuratorHidden'
import { useFeatureAccess } from '../hooks/useFeatureAccess'
import type { FeatureState } from '@tfa/shared'
import { useTelegram } from '../hooks/useTelegram'
import { useCurator } from '../contexts/CuratorContext'
import { useCommentsInbox } from '../hooks/useCommentsInbox'
import { fatSecretStatusRefetchInterval } from '../lib/fatsecretPolling'
import { api, describeApiError } from '../services/api'
import { toast } from 'sonner'
import { isGamificationEnabled, latestWeightEma, weightEmaSeries, formatWeight, mergeWeightSources, measurementCycleStart, nextMeasurementCycleStart, measurementWindowEnd, isMeasurementWindowOpen, canSelfEditProgram } from '@tfa/shared'
import { trainingCardIdleCopy } from '../lib/trainingCardIdle'
import { isWorkoutDraftStale } from '../utils/workoutDraftStale'
import { localDateKey } from '../utils/workoutSessionResume'
import { periodicReportTiles } from '../utils/reportTiles'

// ---- Types ----

interface DiaryMeal {
  id: string
  mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK'
  description?: string
  photoUrl?: string
  calories?: number
  protein?: number
  fat?: number
  carbs?: number
  // #372: поля fiber у приёма НЕТ — клетчатка живёт только в дне
  // (DiaryEntry.fiber). Раньше оно было объявлено здесь, из-за чего код
  // молча читал undefined и показывал «Кл 0» при заполненной клетчатке.
  // Per-product breakdown for FatSecret-synced meals (null for manual meals).
  items?: import('../components/MealItemsBreakdown').MealItem[]
  comments?: CuratorCommentDto[]
}

interface DiaryEntryData {
  id: string
  date: string
  calories?: number
  protein?: number
  fat?: number
  carbs?: number
  fiber?: number
  sleep?: number          // legacy (часы) — больше не пишется, остаётся в API-ответах
  sleepQuality?: 'BAD' | 'SO_SO' | 'GOOD' | null
  steps?: number
  weight?: number
  training?: string
  trainingTime?: number
  trainingStatus?: 'idle' | 'active' | 'done'
  trainingXp?: number
  trainingCoins?: number
  trainingWorkouts?: number
  trainingStreak?: number
  photoUrl?: string
  notes?: string
  meals?: DiaryMeal[]
  comments?: CuratorCommentDto[]
}

// ---- Constants ----

const F = 'Roboto, sans-serif'

const C = {
  primary: '#1A3A5C',
  text: '#1C2D41',
  secondary: '#6B7B8D',
  tertiary: '#9AA5B4',
  todayBg: '#1E3C8C',
  strength: '#4A6CF7',
  cardio: '#24A1DE',
  tasks: '#F59E0B',
  metrics: '#34C759',
  indicators: '#E91E8C',
  accent: '#4A6CF7',
  bg: '#F0F4F8',
  white: '#FFFFFF',
  glass: 'rgba(255,255,255,0.72)',
  kcal: '#4A9FE5',
  protein: '#34C759',
  fat: '#FF9500',
  carbs: '#AF52DE',
  fiber: '#10B981',
  sleepColor: '#8B7FF5',
  stepsColor: '#4A9FE5',
  weightColor: '#A0AEC0',
} as const

const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

const MEAL_LABELS: Record<string, string> = {
  BREAKFAST: 'Завтрак',
  LUNCH: 'Обед',
  DINNER: 'Ужин',
  SNACK: 'Перекус',
}

const MEAL_ACCENT: Record<string, string> = {
  BREAKFAST: '#FF9500',
  LUNCH: '#4A9FE5',
  DINNER: '#6C63FF',
  SNACK: '#AF52DE',
}

const TODAY = new Date()

// ---- Helpers ----

function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * #519: день из ЯРЛЫЧНОЙ даты сервера («2026-09-02T00:00:00.000Z»).
 *
 * Такие даты — не момент времени, а имя дня: их шлют записи дневника,
 * тренировки и замеры. Через `new Date(...)` + локальные геттеры они уезжают:
 * западнее UTC полночь 2-го — это 1-е, 17:00, и кардио клиента из
 * Лос-Анджелеса «улетало» в предыдущий день. Ярлык читаем как есть.
 */
function labelDateKey(iso: string | Date): string {
  const s = typeof iso === 'string' ? iso : iso.toISOString()
  return s.slice(0, 10)
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function getDayOfWeek(d: Date): number {
  return (d.getDay() + 6) % 7
}

function isFuture(d: Date): boolean {
  const todayStart = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate())
  const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return dStart.getTime() > todayStart.getTime()
}

function generateStripDays(center: Date, back: number, forward: number): Date[] {
  const days: Date[] = []
  for (let i = -back; i <= forward; i++) {
    const d = new Date(center)
    d.setDate(center.getDate() + i)
    days.push(d)
  }
  return days
}

function getCalendarMatrix(year: number, month: number): (Date | null)[][] {
  const firstDay = new Date(year, month, 1)
  const startDow = getDayOfWeek(firstDay)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const weeks: (Date | null)[][] = []
  let week: (Date | null)[] = []

  for (let i = 0; i < startDow; i++) week.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(new Date(year, month, d))
    if (week.length === 7) { weeks.push(week); week = [] }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null)
    weeks.push(week)
  }
  return weeks
}

function formatDateRu(d: Date): string {
  const day = d.getDate()
  const monthsGenitive = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
  ]
  const weekdays = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
  return `${day} ${monthsGenitive[d.getMonth()]}, ${weekdays[d.getDay()]}`
}

function isPastDate(d: Date): boolean {
  const todayStart = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate())
  const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return dStart.getTime() < todayStart.getTime()
}

function getMealIcon(type: string) {
  switch (type) {
    case 'BREAKFAST': return <Sunrise size={16} color={MEAL_ACCENT.BREAKFAST} />
    case 'LUNCH': return <Sun size={16} color={MEAL_ACCENT.LUNCH} />
    case 'DINNER': return <Sunset size={16} color={MEAL_ACCENT.DINNER} />
    case 'SNACK': return <Utensils size={16} color={MEAL_ACCENT.SNACK} />
    default: return <Utensils size={16} color={C.tertiary} />
  }
}

// ---- Determine activity progress for a given diary entry ----
// XP-weighted: training=10, nutrition=10, sleep=1, steps=1, weight=1, measurements=10, photos=10.
// База дня — только ежедневные отчёты (питание+сон+шаги+вес = 13 XP): день без
// тренировки «пустой» по тренировке и может закрыться на 100% (13/13). Тренировка
// (+10) ожидается только в день с записанной тренировкой; замеры/фото добавляются
// в день их сдачи — поэтому total динамический, а не фиксированные 23.
function getDayProgress(
  diary: DiaryEntryData | undefined,
  hasMeasurement: boolean,
  hasWorkout: boolean,
  hasPhoto = false,
): { earned: number; total: number; pct: number; color: string } {
  let total = 13 // 10 (питание) + 1 + 1 + 1 — ежедневные отчёты
  let earned = 0
  if (hasWorkout || diary?.training) { total += 10; earned += 10 }
  if ((diary?.meals && diary.meals.length > 0) || diary?.calories != null) earned += 10
  // Поле `sleep` legacy и больше не пишется — новый формат `sleepQuality`.
  // Без проверки обоих ring оставался неполным даже после всех отчётов (bug #82).
  if (diary?.sleepQuality != null || diary?.sleep != null) earned += 1
  if (diary?.steps != null && diary.steps > 0) earned += 1
  if (diary?.weight != null) earned += 1
  if (hasMeasurement) { total += 10; earned += 10 }
  if (hasPhoto) { total += 10; earned += 10 }
  const pct = total > 0 ? (earned / total) * 100 : 0
  const color =
    pct === 0 ? 'transparent'
    : pct < 50 ? '#FF6B6B'
    : pct < 100 ? '#FFA726'
    : '#34C759'
  return { earned, total, pct, color }
}

// Ring around day number — SVG circle stroke that fills clockwise.
function ProgressRing({
  size,
  stroke,
  pct,
  color,
  bgColor = 'rgba(26,58,92,0.10)',
}: {
  size: number
  stroke: number
  pct: number
  color: string
  bgColor?: string
}) {
  const r = (size - stroke) / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circ
  return (
    <svg width={size} height={size} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={bgColor} strokeWidth={stroke} />
      {pct > 0 && color !== 'transparent' && (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
    </svg>
  )
}

// ---- Sub-components ----

function DayStrip({
  selectedDate,
  onSelect,
  onToggleCalendar,
  calendarOpen,
  calendarMonth,
  onCalendarPrev,
  onCalendarNext,
  diaryCache,
  measurementsByDate,
  photosByDate,
  workoutsByDate,
  unreadByDate,
  onOpenComments,
  unreadComments,
}: {
  selectedDate: Date
  onSelect: (d: Date) => void
  onToggleCalendar: () => void
  calendarOpen: boolean
  calendarMonth: Date
  onCalendarPrev: () => void
  onCalendarNext: () => void
  diaryCache: Record<string, DiaryEntryData | undefined>
  measurementsByDate: Set<string>
  photosByDate: Set<string>
  workoutsByDate: Set<string>
  unreadByDate: Record<string, number>
  onOpenComments?: () => void
  unreadComments?: number
}) {
  // 60 назад / 14 вперёд: куратору нужно листать дальше пары недель назад,
  // чтобы видеть всю историю клиента в стрипе (а не только через попап-календарь).
  const days = useMemo(() => generateStripDays(TODAY, 60, 14), [])
  const stripRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Пока месячный календарь открыт — strip скрыт через AnimatePresence (exit
    // motion.div). Эффект перезапустится, когда calendarOpen станет false и
    // strip восстановится в DOM. Без зависимости от calendarOpen и без
    // rAF-retry strip скроллит при clientWidth === 0 (motion.div ещё в
    // height:0 фазе animation), scrollLeft уходит на ~25px и список встаёт в
    // далёкое прошлое — bug #69 «съезжающий календарь».
    if (calendarOpen) return
    let cancelled = false
    const tryScroll = () => {
      if (cancelled) return
      const el = stripRef.current
      if (!el || el.clientWidth === 0) {
        requestAnimationFrame(tryScroll)
        return
      }
      const idx = days.findIndex((d) => isSameDay(d, selectedDate))
      if (idx < 0) return
      const cellW = 50 // 44 + 6 gap
      const scrollTo = idx * cellW - el.clientWidth / 2 + cellW / 2
      el.scrollLeft = Math.max(0, scrollTo)
    }
    requestAnimationFrame(tryScroll)
    return () => { cancelled = true }
  }, [days, selectedDate, calendarOpen])

  const monthLabel = calendarOpen
    ? `${MONTHS_RU[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`
    : `${MONTHS_RU[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`

  return (
    <div>
      {/* Month header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: calendarOpen ? 'space-between' : 'flex-start',
          padding: '10px 16px 4px',
          minHeight: 36,
          gap: 8,
        }}
      >
        {calendarOpen ? (
          <>
            <div onClick={onCalendarPrev} style={{ padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ChevronLeft size={20} color="#1A3A5C" />
            </div>
            <div
              onClick={onToggleCalendar}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: F }}>
                {monthLabel}
              </span>
              <ChevronDown size={14} color={C.secondary} style={{ transform: 'rotate(180deg)' }} />
            </div>
            <div onClick={onCalendarNext} style={{ padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ChevronRight size={20} color="#1A3A5C" />
            </div>
          </>
        ) : (
          <>
            <div onClick={onToggleCalendar} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: C.secondary, fontFamily: F }}>
                {monthLabel}
              </span>
              <ChevronDown size={14} color={C.secondary} />
            </div>
            {/* #266/#270: единый инбокс комментариев куратора — иконка с красным
                счётчиком непрочитанных, рядом с колокольчиком. */}
            {onOpenComments && (
              <button
                data-testid="comments-inbox-icon"
                onClick={(e) => { e.stopPropagation(); onOpenComments() }}
                aria-label="Комментарии куратора"
                style={{
                  position: 'relative', width: 32, height: 32, borderRadius: 10, flexShrink: 0, marginRight: 4,
                  border: 'none', background: 'rgba(108,92,231,0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <MessageSquare size={16} color="#6C5CE7" />
                {(unreadComments ?? 0) > 0 && (
                  <span style={{
                    position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px',
                    borderRadius: 8, background: '#FF3B30', color: 'white', fontSize: 9, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white',
                  }}>{unreadComments}</span>
                )}
              </button>
            )}
            {/* Колокольчик уведомлений в правом углу шапки дневника */}
            <NotificationsBell iconColor="#4A6CF7" accent="#4A6CF7" />
          </>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!calendarOpen ? (
          <motion.div
            key="strip"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              ref={stripRef}
              className="diary-day-scroll"
              style={{
                display: 'flex',
                gap: 6,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                padding: '2px 16px 6px',
                margin: '0 -14px',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {days.map((d) => {
                const isToday = isSameDay(d, TODAY)
                const isSelected = isSameDay(d, selectedDate)
                const future = isFuture(d)
                const dow = getDayOfWeek(d)
                const key = toDateKey(d)
                const progress = getDayProgress(diaryCache[key], measurementsByDate.has(key), workoutsByDate.has(key), photosByDate.has(key))
                const unread = unreadByDate[key] ?? 0

                const borderColor = progress.pct > 0 ? progress.color : C.accent

                return (
                  <motion.div
                    key={key}
                    data-testid={`diary-day-${key}`}
                    data-selected={isSelected ? 'true' : 'false'}
                    onClick={() => onSelect(d)}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      minWidth: 44,
                      width: 44,
                      height: 72,
                      borderRadius: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      cursor: 'pointer',
                      flexShrink: 0,
                      background: isToday
                        ? C.todayBg
                        : isSelected
                          ? C.white
                          : C.glass,
                      border: isSelected && !isToday
                        ? `1.5px solid ${borderColor}`
                        : '1.5px solid transparent',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      transform: isToday ? 'scale(1.08)' : undefined,
                      boxShadow: isToday ? '0 0 12px rgba(30,60,140,0.35)' : undefined,
                      opacity: future ? 0.55 : 1,
                      transition: 'all 0.15s ease',
                      fontFamily: F,
                      position: 'relative',
                    }}
                  >
                    {unread > 0 && (
                      <div style={{
                        position: 'absolute', top: -4, right: -4,
                        minWidth: 16, height: 16, borderRadius: 8,
                        padding: '0 4px',
                        background: '#FF3B30', color: 'white',
                        fontSize: 9, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(255,59,48,0.5)',
                        zIndex: 2, lineHeight: 1,
                      }}>
                        {unread}
                      </div>
                    )}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: isToday ? 'rgba(255,255,255,0.7)' : C.tertiary,
                        lineHeight: 1,
                      }}
                    >
                      {WEEKDAYS_SHORT[dow]}
                    </span>
                    {/* Day number + progress ring */}
                    <div style={{ position: 'relative', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ProgressRing
                        size={30}
                        stroke={2.5}
                        pct={progress.pct}
                        color={isToday ? 'rgba(255,255,255,0.95)' : progress.color}
                        bgColor={isToday ? 'rgba(255,255,255,0.18)' : 'rgba(26,58,92,0.10)'}
                      />
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: isToday ? C.white : C.text,
                          lineHeight: 1,
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {d.getDate()}
                      </span>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="calendar"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <InlineMonthCalendar
              selectedDate={selectedDate}
              calendarMonth={calendarMonth}
              onSelect={onSelect}
              diaryCache={diaryCache}
              measurementsByDate={measurementsByDate}
              photosByDate={photosByDate}
              workoutsByDate={workoutsByDate}
              unreadByDate={unreadByDate}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function InlineMonthCalendar({
  selectedDate,
  calendarMonth,
  onSelect,
  diaryCache,
  measurementsByDate,
  photosByDate,
  workoutsByDate,
  unreadByDate,
}: {
  selectedDate: Date
  calendarMonth: Date
  onSelect: (d: Date) => void
  diaryCache: Record<string, DiaryEntryData | undefined>
  measurementsByDate: Set<string>
  photosByDate: Set<string>
  workoutsByDate: Set<string>
  unreadByDate: Record<string, number>
}) {
  const weeks = useMemo(
    () => getCalendarMatrix(calendarMonth.getFullYear(), calendarMonth.getMonth()),
    [calendarMonth]
  )

  return (
    <div style={{ fontFamily: F }}>
      {/* Weekday headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {WEEKDAYS_SHORT.map((wd) => (
          <div
            key={wd}
            style={{
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 600,
              color: C.tertiary,
              padding: '4px 0',
            }}
          >
            {wd}
          </div>
        ))}
      </div>
      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {week.map((d, di) => {
            if (!d) return <div key={di} />
            const isToday = isSameDay(d, TODAY)
            const isSelected = isSameDay(d, selectedDate)
            const key = toDateKey(d)
            const progress = getDayProgress(diaryCache[key], measurementsByDate.has(key), workoutsByDate.has(key), photosByDate.has(key))
            const unread = unreadByDate[key] ?? 0
            return (
              <div
                key={di}
                data-testid={`diary-day-${key}`}
                data-selected={isSelected ? 'true' : 'false'}
                onClick={() => onSelect(d)}
                style={{
                  textAlign: 'center',
                  padding: '6px 0',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: isToday || isSelected ? 700 : 400,
                  color: isToday ? C.white : C.text,
                  background: isToday
                    ? C.todayBg
                    : isSelected
                      ? 'rgba(30,60,140,0.08)'
                      : 'transparent',
                  border: isSelected && !isToday ? `1.5px solid ${C.todayBg}` : '1.5px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {unread > 0 && (
                  <div style={{
                    position: 'absolute', top: 0, right: 0,
                    minWidth: 16, height: 16, borderRadius: 8,
                    padding: '0 4px',
                    background: '#FF3B30', color: 'white',
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid white',
                    boxShadow: '0 2px 4px rgba(255,59,48,0.55)',
                    zIndex: 2, lineHeight: 1,
                  }}>
                    {unread > 99 ? '99+' : unread}
                  </div>
                )}
                <div style={{ position: 'relative', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ProgressRing
                    size={32}
                    stroke={2.5}
                    pct={progress.pct}
                    color={isToday ? 'rgba(255,255,255,0.95)' : progress.color}
                    bgColor={isToday ? 'rgba(255,255,255,0.18)' : 'rgba(26,58,92,0.10)'}
                  />
                  <span style={{ position: 'relative', zIndex: 1 }}>{d.getDate()}</span>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

function DateContextBanner({
  selectedDate,
  onGoToToday,
}: {
  selectedDate: Date
  onGoToToday: () => void
}) {
  if (isSameDay(selectedDate, TODAY)) return null

  const past = isPastDate(selectedDate)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 40,
        fontFamily: F,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: past ? '#34C759' : '#4A9FE5', flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>
          {formatDateRu(selectedDate)}
        </span>
      </div>
      <span
        onClick={onGoToToday}
        style={{ fontSize: 12, fontWeight: 600, color: C.strength, cursor: 'pointer' }}
      >
        Сегодня
      </span>
    </div>
  )
}

type NutritionGoalView = {
  dayType: 'REGULAR' | 'TRAINING'
  calories: number
  protein: number
  fat: number
  carbs: number
  fiber?: number | null
  curatorName?: string | null
} | null

function NutritionCarousel({
  meals,
  goal,
  onOpen,
}: {
  meals: DiaryMeal[]
  goal: NutritionGoalView
  onOpen: (type: DiaryMeal['mealType']) => void
}) {
  const hasGoal = !!goal && (goal.calories ?? 0) > 0
  const calorieTarget = goal?.calories ?? 2200
  const dayLabel = goal?.dayType === 'TRAINING' ? 'Тренировочный день' : 'Обычный день'
  const dayEmoji = goal?.dayType === 'TRAINING' ? '🔥' : '🌙'
  const mealTypes = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'] as const
  const mealMap = useMemo(() => {
    const map: Record<string, DiaryMeal | undefined> = {}
    for (const m of meals) map[m.mealType] = m
    return map
  }, [meals])
  // Когда у одного типа несколько meal'ов (ручной + FatSecret-импорт),
  // mealMap показывает только последний — комменты к остальным теряются.
  // Поэтому собираем все комменты по mealType отдельно.
  const commentsByType = useMemo(() => {
    const map: Record<string, CuratorCommentDto[]> = { BREAKFAST: [], LUNCH: [], DINNER: [], SNACK: [] }
    for (const m of meals) {
      if (m.comments?.length) map[m.mealType].push(...m.comments)
    }
    return map
  }, [meals])
  const calories = useMemo(
    () => meals.reduce((sum, m) => sum + (m.calories ?? 0), 0),
    [meals],
  )

  return (
    <div data-testid="nutrition-carousel">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: F }}>
          Питание
        </span>
        <span style={{ fontSize: 12, color: C.tertiary, fontFamily: F }}>
          {hasGoal
            ? `${Math.round(calories).toLocaleString('ru-RU')} / ${calorieTarget.toLocaleString('ru-RU')} ккал`
            : `${Math.round(calories).toLocaleString('ru-RU')} ккал`}
        </span>
      </div>
      {goal && (
        <>
          <div
            data-testid="nutrition-curator-goal"
            style={{
              background: 'rgba(255,255,255,0.70)',
              border: '1px solid rgba(255,255,255,0.45)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: 12,
              padding: '8px 12px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              fontFamily: F,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                Цель куратора · {dayEmoji} {dayLabel}
              </div>
              <div style={{ fontSize: 11, color: C.tertiary, marginTop: 2 }}>
                <span style={{ color: C.protein, fontWeight: 700 }}>Б</span> {goal.protein}г{'  '}
                <span style={{ color: C.fat, fontWeight: 700 }}>Ж</span> {goal.fat}г{'  '}
                <span style={{ color: C.carbs, fontWeight: 700 }}>У</span> {goal.carbs}г
                {goal.fiber != null && (<>{'  '}<span style={{ color: C.fiber, fontWeight: 700 }}>Кл</span> {goal.fiber}г</>)}
              </div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>
              {goal.calories.toLocaleString('ru-RU')} ккал
            </div>
          </div>
        </>
      )}
      <div
        className="diary-day-scroll"
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '2px 24px 4px 0',
          scrollSnapType: 'x mandatory',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {mealTypes.map((type) => {
          const meal = mealMap[type]
          const hasData = meal && (meal.description || meal.calories != null)
          const accentColor = MEAL_ACCENT[type]
          const aggregatedComments = commentsByType[type] ?? []
          const unreadCount = aggregatedComments.filter((c) => c.isUnread).length
          const lastComment = aggregatedComments[aggregatedComments.length - 1]

          if (hasData) {
            return (
              <div
                key={type}
                data-testid={`meal-card-${type}`}
                data-state="filled"
                onClick={() => onOpen(type)}
                style={{
                  minWidth: 148,
                  width: 148,
                  padding: 12,
                  borderRadius: 14,
                  scrollSnapAlign: 'start',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  flexShrink: 0,
                  background: C.glass,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderTop: `3px solid ${accentColor}`,
                  fontFamily: F,
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {/* Edit hint */}
                <div style={{ position: 'absolute', top: 7, right: 7, width: 17, height: 17, borderRadius: '50%', background: `${accentColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pencil size={9} color={accentColor} />
                </div>
                {unreadCount > 0 && (
                  <div style={{
                    position: 'absolute', top: -5, left: -5,
                    minWidth: 18, height: 18, borderRadius: 9,
                    padding: '0 5px',
                    background: '#FF3B30', color: 'white',
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(255,59,48,0.5)',
                  }}>
                    {unreadCount}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {getMealIcon(type)}
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.text, paddingRight: 18 }}>
                    {MEAL_LABELS[type]}
                  </span>
                </div>
                {meal.description && (
                  <span
                    style={{
                      fontSize: 11,
                      color: C.secondary,
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {meal.description}
                  </span>
                )}
                {meal.calories != null && (
                  <span data-testid="meal-card-calories" style={{ fontSize: 12, fontWeight: 700, color: accentColor }}>
                    {meal.calories} kcal
                  </span>
                )}
                {lastComment && (
                  <div
                    data-testid="meal-card-comment-preview"
                    style={{
                      marginTop: 2,
                      padding: '6px 8px',
                      borderRadius: 8,
                      background: unreadCount > 0 ? 'rgba(74,108,247,0.14)' : 'rgba(74,108,247,0.06)',
                      border: `1px solid ${unreadCount > 0 ? 'rgba(74,108,247,0.35)' : 'rgba(74,108,247,0.18)'}`,
                      fontSize: 10,
                      lineHeight: 1.35,
                      color: '#1A3A5C',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    <span style={{ fontWeight: 700, color: '#4A6CF7' }}>💬 Куратор:</span>{' '}
                    {lastComment.text}
                  </div>
                )}
              </div>
            )
          }

          return (
            <div
              key={type}
              data-testid={`meal-card-${type}`}
              data-state="empty"
              onClick={() => onOpen(type)}
              style={{
                minWidth: 148,
                width: 148,
                padding: 12,
                borderRadius: 14,
                border: '1.5px dashed #C4CDD5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                cursor: 'pointer',
                scrollSnapAlign: 'start',
                flexShrink: 0,
                background: 'rgba(255,255,255,0.35)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                transition: 'border-color 0.15s',
                fontFamily: F,
              }}
            >
              {getMealIcon(type)}
              <span style={{ fontSize: 12, fontWeight: 600, color: C.secondary }}>
                {MEAL_LABELS[type]}
              </span>
              <span style={{ fontSize: 11, color: C.accent, fontWeight: 500 }}>
                + Добавить
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface ProgramExercise {
  id: string
  exerciseId: string | null
  exercise: { id: string; name: string } | null
  customName?: string | null
  orderNum: number
}

interface ProgramDay {
  id: string
  dayNumber: number
  exercises: ProgramExercise[]
}

function TrainingCard({
  diary,
  status: statusProp,
  startedAt,
  workoutEntry,
  aggregates,
  onNavigate,
  onEdit,
  onOpenWorkouts,
  todayExercises,
  unreadComments = 0,
  videoTechniqueDone = false,
  dayNumber,
  isCuratorViewing = false,
  isAdminView = false,
  hasProgram = true,
  onEditProgram,
}: {
  diary: DiaryEntryData | null | undefined
  /** Override the derived status (active/done) from the surrounding page */
  status?: 'idle' | 'active' | 'done'
  /** Absolute timestamp (ms) of when the active workout was started — drives the live timer */
  startedAt?: number
  /** Saved workout entry for this day, used to render the done state */
  workoutEntry?: { id: string; durationMinutes?: number | null; exercises?: Array<{ id: string; sets?: Array<unknown> }>; cardioActivities?: Array<unknown> } | null
  /** Aggregate workout stats for the stats row (count / avg duration / current streak) */
  aggregates?: { count: number; avgMin: number; streak: number }
  onNavigate: () => void
  onEdit?: () => void
  /** Open the Workouts overview (program / history) — used in done state */
  onOpenWorkouts?: () => void
  todayExercises?: ProgramExercise[]
  unreadComments?: number
  /** Клиент сдал сегодня видео техники (показываем отметку на блоке). */
  videoTechniqueDone?: boolean
  /** Program day number for the status badge ("День N") */
  dayNumber?: number
  /** Curator is viewing a client's diary — replaces client CTAs with program-edit actions */
  isCuratorViewing?: boolean
  /** Админ «за пользователя» (только чтение): программа открывается на просмотр, не на правку */
  isAdminView?: boolean
  /** Whether the client has an active workout program (relevant in curator mode) */
  hasProgram?: boolean
  /** Navigate to the program editor (curator mode only) */
  onEditProgram?: () => void
}) {
  const status = statusProp ?? diary?.trainingStatus ?? (diary?.training ? 'done' : 'idle')
  const { user } = useAuth()
  const rewardsOn = isGamificationEnabled((user as any)?.mode ?? null, (user as any)?.completedAt ?? null)
  // #605: без куратора программу выбирают сами — подпись и кнопка зовут выбрать.
  const idleCopy = trainingCardIdleCopy(
    !isCuratorViewing && !hasProgram && canSelfEditProgram((user as any)?.product ?? null, (user as any)?.access ?? null),
  )

  const gradients: Record<string, string> = {
    idle: 'linear-gradient(135deg, #1A3A5C 0%, #4A6CF7 100%)',
    active: 'linear-gradient(135deg, #B45309 0%, #FF9500 100%)',
    done: 'linear-gradient(135deg, #1B5E20 0%, #34C759 100%)',
  }

  // Aggregate stats (count / avg time / streak) — fed from real workout entries.
  const workouts = aggregates?.count ?? diary?.trainingWorkouts ?? 0
  const avgTimeMin = aggregates?.avgMin ?? diary?.trainingTime ?? 0
  const streak = aggregates?.streak ?? diary?.trainingStreak ?? 0
  // Per-entry duration (used by the done state's "1 ч 24 мин" line, not the stats row).
  const timeMin = workoutEntry?.durationMinutes ?? diary?.trainingTime ?? 0
  const xp = rewardsOn ? (diary?.trainingXp ?? 0) : 0
  const coins = rewardsOn ? (diary?.trainingCoins ?? 0) : 0
  // Считаем только упражнения с хотя бы одним зафиксированным подходом —
  // в entry могут лежать «открытые но не выполненные» строки из плана
  // программы, и они не должны попадать в счётчик «N упражнений · M подходов»
  // (bug #83 — подходы уже считались правильно, упражнения — нет).
  const doneExerciseCount = workoutEntry?.exercises?.filter(
    (ex) => (ex.sets?.length ?? 0) > 0,
  ).length ?? 0
  const doneSetCount = workoutEntry?.exercises?.reduce(
    (sum, ex) => sum + (ex.sets?.length ?? 0),
    0,
  ) ?? 0
  const doneIsCardioOnly = !!workoutEntry && doneExerciseCount === 0 && (workoutEntry.cardioActivities?.length ?? 0) > 0
  const doneTitle = doneIsCardioOnly ? 'Кардио тренировка' : 'Силовая тренировка'

  // Active-state live ticker. Elapsed is computed from the absolute `startedAt`
  // timestamp persisted in the workout-draft, so the timer reflects real time
  // since the workout began — it stays correct across diary tab remounts.
  // The `_tick` state only forces a re-render every second; the value itself
  // is derived from Date.now() - startedAt.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (status !== 'active') return
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [status])
  const elapsed = status === 'active' && startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
    : timeMin * 60
  const hh = String(Math.floor(elapsed / 3600)).padStart(2, '0')
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')
  const ss = String(elapsed % 60).padStart(2, '0')

  // Done-state duration formatting: "1 ч 24 мин" / "42 мин"
  const doneDuration = timeMin >= 60
    ? `${Math.floor(timeMin / 60)} ч ${timeMin % 60} мин`
    : `${timeMin} мин`

  // Status badge: prototype shows "День N" / "В процессе" / "✓ Выполнено"
  const statusBadge = status === 'done'
    ? { text: '✓ Выполнено', dot: null as string | null }
    : status === 'active'
      ? { text: 'В процессе', dot: '#FFD60A' }
      : { text: dayNumber ? `День ${dayNumber}` : 'Сегодня', dot: '#34C759' }

  return (
    <div
      style={{
        borderRadius: 20,
        padding: 18,
        background: gradients[status],
        color: C.white,
        fontFamily: F,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {unreadComments > 0 && (
        <div
          data-testid="training-unread-comments-badge"
          style={{
            position: 'absolute', top: -6, left: -6,
            height: 26, borderRadius: 13,
            padding: '0 9px 0 6px',
            background: '#FF3B30', color: 'white',
            fontSize: 12, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            boxShadow: '0 2px 10px rgba(255,59,48,0.55)',
            zIndex: 2,
            border: '1.5px solid white',
            animation: 'unreadPulseScale 1.6s ease-in-out infinite',
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          <span style={{ lineHeight: 1 }}>{unreadComments > 99 ? '99+' : unreadComments}</span>
        </div>
      )}
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: 40, background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ position: 'absolute', bottom: -15, right: 30, width: 50, height: 50, borderRadius: 25, background: 'rgba(255,255,255,0.04)' }} />
      {/* TFA brand watermark — крупное полупрозрачное лого справа-сверху */}
      <div style={{
        position: 'absolute', top: -20, right: -30,
        pointerEvents: 'none',
        transform: 'rotate(-12deg)',
      }}>
        <TfaLogo size={180} tint="white" opacity={0.10} />
      </div>

      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dumbbell size={18} color={C.white} />
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.9 }}>
            Тренировка
          </span>
          {videoTechniqueDone && (
            <span
              data-testid="training-video-technique-done"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.22)', fontSize: 10, fontWeight: 700, color: 'white' }}
            >
              🎥 Техника сдана
            </span>
          )}
        </div>
        <div style={{ padding: '4px 10px', borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'white' }}>{statusBadge.text}</span>
          {statusBadge.dot && (
            <motion.span
              animate={status === 'active' ? { opacity: [1, 0.3, 1] } : {}}
              transition={status === 'active' ? { duration: 1.4, repeat: Infinity } : {}}
              style={{ width: 6, height: 6, borderRadius: 3, background: statusBadge.dot, display: 'inline-block' }}
            />
          )}
        </div>
      </div>

      {/* Content by status */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {status === 'idle' && (
          <>
            {todayExercises && todayExercises.length > 0 ? (
              <div>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                  {todayExercises.slice(0, 3).map((e) => e.exercise?.name || e.customName || 'Упражнение').join(' · ')}
                </p>
                {todayExercises.length > 3 && (
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: '4px 0 0' }}>
                    + {todayExercises.length - 3} упражнени{todayExercises.length - 3 === 1 ? 'е' : todayExercises.length - 3 < 5 ? 'я' : 'й'}
                  </p>
                )}
              </div>
            ) : diary?.training ? (
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', margin: 0 }}>{diary.training}</p>
            ) : (
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', margin: 0 }}>{idleCopy.text}</p>
            )}
          </>
        )}

        {status === 'active' && (
          <>
            <p style={{ fontSize: 28, fontWeight: 700, color: 'white', textAlign: 'center', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Clock size={22} color="rgba(255,255,255,0.75)" /> {hh}:{mm}:{ss}
            </p>
            {todayExercises && todayExercises.length > 0 && (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', textAlign: 'center', margin: '6px 0 0' }}>
                {todayExercises.slice(0, 3).map((e) => e.exercise?.name || e.customName || 'Упражнение').join(' · ')}
                {todayExercises.length > 3 ? ` + ${todayExercises.length - 3}` : ''}
              </p>
            )}
          </>
        )}

        {status === 'done' && (
          <>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
              {diary?.training || doneTitle}
            </p>
            {timeMin > 0 && (
              <p style={{ fontSize: 20, fontWeight: 700, color: 'white', margin: '4px 0 2px' }}>{doneDuration}</p>
            )}
            {doneExerciseCount > 0 && (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                {doneExerciseCount} упражнени{doneExerciseCount === 1 ? 'е' : doneExerciseCount < 5 ? 'я' : 'й'}
                {doneSetCount > 0 ? ` · ${doneSetCount} подход${doneSetCount === 1 ? '' : doneSetCount < 5 ? 'а' : 'ов'}` : ''}
              </p>
            )}
          </>
        )}
      </div>

      {/* Divider + stats (idle/active) or reward badges (done).
          Stats row is hidden when the user has zero workouts at all — keeps the
          card clean for first-timers; once they save a workout the row appears. */}
      {status !== 'done' && workouts > 0 ? (
        <>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', margin: '16px 0' }} />
          <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
            {[
              { Icon: Flame, value: String(workouts), label: 'тренировок' },
              { Icon: Clock, value: avgTimeMin > 0 ? `${Math.floor(avgTimeMin / 60)}:${String(avgTimeMin % 60).padStart(2, '0')}` : '—', label: 'ср. время' },
              { Icon: Flame, value: String(streak), label: 'в режиме' },
            ].map((s, i, arr) => (
              <div
                key={s.label}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.2)' : undefined,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 2 }}>
                  <s.Icon size={14} color="rgba(255,255,255,0.75)" />
                  <span style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>{s.value}</span>
                </div>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </>
      ) : status === 'done' ? (
        <>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', margin: '16px 0' }} />
          <div
            data-testid="training-rewards"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}
          >
            {/* Always show «Сдано» chip when training is done — confirms the
                report registered even if XP/coins aren't surfaced yet. */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              borderRadius: 14, padding: '6px 12px',
              background: 'rgba(255,255,255,0.92)',
              color: '#1C9E44',
              fontSize: 12, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase',
              boxShadow: '0 4px 12px rgba(52,199,89,0.28)',
            }}>
              <CheckCircle size={13} color="#1C9E44" /> Сдано
            </span>
            {rewardsOn && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                borderRadius: 14, padding: '6px 12px',
                background: 'linear-gradient(135deg, #FFD60A 0%, #FF9500 100%)',
                color: '#1C2D41',
                fontSize: 13, fontWeight: 800,
                boxShadow: '0 4px 12px rgba(255,149,0,0.32)',
              }}>
                +{xp > 0 ? xp : 5} XP
              </span>
            )}
            {coins > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                borderRadius: 14, padding: '6px 12px',
                background: 'linear-gradient(135deg, #AF52DE 0%, #7C3AED 100%)',
                color: 'white',
                fontSize: 13, fontWeight: 800,
                boxShadow: '0 4px 12px rgba(175,82,222,0.28)',
              }}>
                +{coins} <TuCoinIcon size={14} />
              </span>
            )}
          </div>
        </>
      ) : null}

      {/* Main CTA */}
      <div style={{ marginTop: 16, position: 'relative', zIndex: 1 }}>
        {isCuratorViewing ? (
          <button
            data-testid="diary-program-cta"
            onClick={onEditProgram}
            style={{
              width: '100%', height: 52, borderRadius: 14,
              border: 'none', background: 'rgba(255,255,255,0.92)',
              color: '#1A3A5C', fontSize: 16, fontWeight: 700,
              fontFamily: F, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {isAdminView ? (
              <>
                <Dumbbell size={18} color="#1A3A5C" /> Открыть программу
              </>
            ) : hasProgram ? (
              <>
                <Pencil size={18} color="#1A3A5C" /> Изменить программу
              </>
            ) : (
              <>
                <Plus size={18} color="#1A3A5C" /> Назначить программу
              </>
            )}
          </button>
        ) : status === 'done' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={onOpenWorkouts}
              style={{
                width: '100%', height: 52, borderRadius: 14,
                border: 'none', background: 'rgba(255,255,255,0.92)',
                color: '#1A3A5C', fontSize: 16, fontWeight: 700,
                fontFamily: F, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Dumbbell size={18} color="#1A3A5C" /> Открыть тренировки
            </button>
            <button
              onClick={onEdit || onNavigate}
              style={{
                width: '100%', height: 44, borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.35)', background: 'transparent',
                color: 'white', fontSize: 15, fontWeight: 600,
                fontFamily: F, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Pencil size={16} color="white" /> Редактировать
            </button>
          </div>
        ) : (
          <button
            onClick={onNavigate}
            style={{
              width: '100%', height: 52, borderRadius: 14,
              border: 'none', background: 'rgba(255,255,255,0.92)',
              color: '#1A3A5C', fontSize: 16, fontWeight: 700,
              fontFamily: F, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {status === 'active' ? (
              <>
                <Play size={18} color="#1A3A5C" fill="#1A3A5C" /> Продолжить тренировку
              </>
            ) : (
              <>
                <Dumbbell size={18} color="#1A3A5C" /> {idleCopy.cta}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Periodic report card (Замеры / Фото прогресса) ──
// state:
//   'open'    — окно сдачи открыто, активно показываем (под блоком отчётов)
//   'done'    — сдано, под тренировкой; показываем дату следующего открытия
//   'closed'  — окно ещё не открыто; счётчик до открытия (компактно)
function PeriodicReportCard({
  title,
  Icon,
  color,
  state,
  xp,
  earnedXp,
  lastSubmittedAt,
  nextOpenAt,
  windowEndAt,
  missedWindowEndAt,
  onClick,
  testId,
}: {
  title: string
  Icon: typeof Camera
  color: string
  state: 'open' | 'done' | 'closed' | 'overdue'
  xp: number
  earnedXp?: number
  lastSubmittedAt?: Date | null
  nextOpenAt?: Date | null
  windowEndAt?: Date | null
  /** #557: последнее пропущенное окно — «Закрылось dd.mm · откроется dd.mm». */
  missedWindowEndAt?: Date | null
  /** Если undefined — карточка некликабельна (read-only заглушка). */
  onClick?: () => void
  /**
   * #509: e2e проверяет состояние окна по самой карточке. В закрытом окне
   * (Вт–Пт, замеры не сданы) пилюли в дневной шкале нет, и сверяться больше
   * не с чем — поэтому карточка отдаёт и testid, и своё состояние.
   */
  testId?: string
}) {
  const fmt = (d: Date) => d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
  const daysUntil = (d: Date) => Math.max(0, Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))

  const isActive = state === 'open' || state === 'overdue'
  // Окно сдачи закрывается сегодня или завтра → акцент SOS-красным.
  const isLastDay = state === 'open' && !!windowEndAt && daysUntil(windowEndAt) <= 1
  const accentColor = state === 'overdue' || isLastDay
    ? '#FF3B30'
    : state === 'done' ? '#34C759' : color

  let statusLabel = ''
  let subtitle = ''
  if (state === 'open') {
    statusLabel = isLastDay ? 'Срочно' : 'Сдать'
    if (windowEndAt) {
      const days = daysUntil(windowEndAt)
      subtitle = isLastDay
        ? (days === 0 ? '⏰ Сегодня последний день!' : '⏰ Остался 1 день — успей сдать!')
        : `до ${fmt(windowEndAt)} · ${days} дн.`
    }
  } else if (state === 'overdue') {
    statusLabel = 'Просрочен'
    if (windowEndAt) subtitle = `Окно закрылось ${fmt(windowEndAt)}`
  } else if (state === 'done') {
    statusLabel = 'Сдан'
    if (nextOpenAt) {
      subtitle = `следующий ${fmt(nextOpenAt)}`
    } else if (lastSubmittedAt) {
      subtitle = `Сдан ${fmt(lastSubmittedAt)}`
    }
  } else if (state === 'closed') {
    // Не показываем «Закрыт» — оставляем пустой бейдж и просто subtitle с датой открытия.
    statusLabel = ''
    if (nextOpenAt) {
      subtitle = missedWindowEndAt
        ? `Закрылось ${fmt(missedWindowEndAt)} · откроется ${fmt(nextOpenAt)}`
        : `Откроется ${fmt(nextOpenAt)}`
    }
  }

  // Если родитель не передал onClick (например, замеры в `closed`/`done`) —
  // карточка ведёт себя как read-only: курсор default + класс не подсвечивается.
  const interactive = !!onClick
  return (
    <div
      data-testid={testId}
      data-state={state}
      onClick={onClick}
      className={
        isLastDay
          ? 'periodic-card-urgent'
          : (interactive && (state === 'open' || state === 'overdue'))
            ? 'periodic-card-active'
            : undefined
      }
      style={{
        borderRadius: 14,
        padding: '10px 10px 9px',
        // #1: заливка как у блока «Отчёты» (единый вид) — цвет несёт только рамка.
        background: 'rgba(255,255,255,0.75)',
        border: `1.5px ${state === 'closed' ? 'dashed' : 'solid'} ${
          state === 'done' ? 'rgba(52,199,89,0.55)'
            : isActive ? accentColor
            : 'rgba(160,174,192,0.40)'
        }`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        cursor: interactive ? 'pointer' : 'default',
        fontFamily: F,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        position: 'relative',
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(26,58,92,0.05)',
      }}
    >
      {/* Top row: icon + status + xp */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{
          position: 'relative',
          width: 28, height: 28, borderRadius: 9,
          background: isActive ? `${accentColor}26` : state === 'done' ? 'rgba(52,199,89,0.15)' : 'rgba(160,174,192,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {/* SOS-style pulse — only on last-day */}
          {isLastDay && (
            <span style={{
              position: 'absolute', inset: 0, borderRadius: 9,
              animation: 'periodic-urgent-pulse 1.8s ease-out infinite',
              pointerEvents: 'none',
            }} />
          )}
          {state === 'done'
            ? <CheckCircle size={15} color="#34C759" />
            : <Icon size={15} color={isActive ? accentColor : '#A0AEC0'} />}
        </div>
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: 8, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
          color: state === 'done' ? '#34C759' : state === 'overdue' ? '#FF3B30' : isActive ? accentColor : C.tertiary,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {statusLabel}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 800,
          color: state === 'done' ? '#34C759' : isActive ? accentColor : C.tertiary,
          flexShrink: 0,
          lineHeight: 1,
        }}>
          +{state === 'done' ? (earnedXp ?? xp) : xp} XP
        </span>
      </div>

      {/* Title */}
      <div style={{
        fontSize: 13, fontWeight: 700, color: C.text,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {title}
      </div>

      {/* Subtitle */}
      <div style={{
        fontSize: 10,
        color: (isLastDay || state === 'overdue') ? '#FF3B30' : C.tertiary,
        fontWeight: isLastDay ? 700 : 400,
        lineHeight: 1.3,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {subtitle}
      </div>
    </div>
  )
}

// ── Карточка «Допа» (анкета обратной связи / NPS) — показывает награду в TuCoin ──
function ExtraSurveyCard({
  title,
  Icon,
  color,
  claimed,
  tucoin,
  onClick,
  onDismiss,
}: {
  title: string
  Icon: typeof Camera
  color: string
  claimed: boolean
  tucoin: number
  onClick?: () => void
  onDismiss?: () => void
}) {
  const interactive = !!onClick && !claimed
  const accent = claimed ? '#34C759' : color
  return (
    <div
      onClick={interactive ? onClick : undefined}
      className={interactive ? 'periodic-card-active' : undefined}
      style={{
        borderRadius: 14, padding: '10px 10px 9px',
        background: claimed
          ? 'rgba(52,199,89,0.06)'
          : `linear-gradient(135deg, ${accent}1A 0%, ${accent}08 100%)`,
        border: `1px solid ${claimed ? 'rgba(52,199,89,0.30)' : `${accent}55`}`,
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        cursor: interactive ? 'pointer' : 'default', fontFamily: F,
        display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', minWidth: 0,
        boxShadow: claimed ? 'none' : `0 4px 14px ${accent}22`,
      }}
    >
      {claimed && onDismiss && (
        <button
          data-testid="survey-dismiss-btn"
          onClick={(e) => { e.stopPropagation(); onDismiss() }}
          aria-label="Скрыть"
          style={{
            position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 11,
            border: 'none', background: 'rgba(26,58,92,0.08)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
        >
          <X size={13} color="#6B7B8D" />
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 9,
          background: claimed ? 'rgba(52,199,89,0.15)' : `${accent}26`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {claimed ? <CheckCircle size={15} color="#34C759" /> : <Icon size={15} color={accent} />}
        </div>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 8, fontWeight: 800, letterSpacing: 0.4,
          textTransform: 'uppercase', color: accent,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {claimed ? 'Получено' : 'Заполнить'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, color: accent, flexShrink: 0, lineHeight: 1 }}>
          +{tucoin} TuCoin
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {title}
      </div>
      <div style={{ fontSize: 10, color: C.tertiary, lineHeight: 1.3 }}>
        {claimed ? 'Спасибо за ответы!' : 'Доступно сейчас'}
      </div>
    </div>
  )
}

// ── Compact nutrition widget: summary bar + expandable detail (FS list or manual entry) ──
function CompactNutritionWidget({
  diary,
  meals,
  goal,
  fatSecretConnected,
  fatSecretState = 'open',
  onOpenMeal,
  onOpenManual,
  onConnectFS,
  onSync,
  isSyncing,
  hasUnreadComment,
  onOpenCommentsHistory,
  onEditGoal,
}: {
  diary: DiaryEntryData | null
  meals: DiaryMeal[]
  goal: NutritionGoalView
  fatSecretConnected: boolean
  /** Три тарифа (#546): 'gray' — синхронизация видна, но выключена; 'off' — иконки нет. */
  fatSecretState?: FeatureState
  onOpenMeal: (type: DiaryMeal['mealType']) => void
  onOpenManual: () => void
  onConnectFS: () => void
  onSync?: () => void
  isSyncing?: boolean
  hasUnreadComment?: boolean
  onOpenCommentsHistory?: () => void
  /** #537: норму задаёт сам клиент (тариф без куратора) — открыть шторку «Моя норма». */
  onEditGoal?: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  // При подключённом FatSecret раскрытие — состояние по умолчанию: показываем
  // КБЖУ/Норму/Историю и блок «Заполни питание» сразу, без тапа. Свернуть можно
  // (шеврон) — тогда блок «заполни питание»/«вручную» прячется, интерфейс сжимается.
  useEffect(() => {
    if (fatSecretConnected) setExpanded(true)
  }, [fatSecretConnected])

  // #269: подсказка «Заполни питание в FatSecret» мозолила глаза при каждом
  // переключении вкладок — её можно свернуть. #332: localStorage в Telegram
  // WebView не переживает перезаход/хард-резет, поэтому источник истины —
  // сервер (ClientSettings.uiFlags.fsHintHidden); localStorage остаётся лишь
  // мгновенным кэшем до прихода настроек.
  const { data: uiSettings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
    staleTime: 60_000,
  })
  const [fsHintHidden, setFsHintHidden] = useState(() => {
    try { return localStorage.getItem('tfa:fs-hint-hidden') === '1' } catch { return false }
  })
  // Сервер главнее локального кэша: пришли настройки — применяем их значение.
  useEffect(() => {
    const server = (uiSettings?.uiFlags as { fsHintHidden?: boolean } | undefined)?.fsHintHidden
    if (typeof server === 'boolean') {
      setFsHintHidden(server)
      try { localStorage.setItem('tfa:fs-hint-hidden', server ? '1' : '0') } catch { /* ignore */ }
    }
  }, [uiSettings])
  const toggleFsHint = (hidden: boolean) => {
    setFsHintHidden(hidden)
    try { localStorage.setItem('tfa:fs-hint-hidden', hidden ? '1' : '0') } catch { /* ignore */ }
    // Персист на сервер — fire-and-forget, при ошибке останется локальный кэш.
    api.updateSettings({ uiFlags: { fsHintHidden: hidden } }).catch(() => { /* ignore */ })
  }
  // #269: кнопка ручного обновления FS — остаётся и в развёрнутом, и в свёрнутом виде.
  const fsSyncBtn = (
    <button
      data-testid="fatsecret-manual-sync-btn"
      onClick={(e) => { e.stopPropagation(); onSync?.() }}
      disabled={isSyncing}
      aria-label="Обновить данные из FatSecret"
      title="Обновить из FatSecret"
      style={{
        flexShrink: 0,
        width: 40, height: 40, borderRadius: 12, padding: 0,
        background: 'rgba(92,168,61,0.16)', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: isSyncing ? 'wait' : 'pointer', opacity: isSyncing ? 0.7 : 1,
      }}
    >
      <RefreshCw size={18} color="#3F7E2A" style={{ animation: isSyncing ? 'spin 0.9s linear infinite' : undefined }} />
    </button>
  )

  // Aggregate totals: prefer meals if present, else fall back to manual diary day-totals.
  const totals = useMemo(() => {
    if (meals.length > 0) {
      // #361: суммируем ПО ОДНОМУ приёму на тип — так же, как их показывают
      // карточки (mealMap: последний побеждает). Раньше складывались ВСЕ строки,
      // а у одного типа их бывает две: ручная и пришедшая из FatSecret — синк
      // удаляет только свои прошлые записи, ручную не трогает. Клиент вписал
      // обед/ужин/перекус руками, потом подтянулся FatSecret — и итог удваивался
      // (2126 + 1475 = 3601 при фактических 2126), причём лишней строки не видно:
      // в карточках отображается только одна на тип.
      const byType: Record<string, DiaryMeal> = {}
      for (const m of meals) byType[m.mealType] = m
      const sum = Object.values(byType).reduce(
        (acc, m) => ({
          calories: acc.calories + (m.calories ?? 0),
          protein: acc.protein + (m.protein ?? 0),
          fat: acc.fat + (m.fat ?? 0),
          carbs: acc.carbs + (m.carbs ?? 0),
        }),
        { calories: 0, protein: 0, fat: 0, carbs: 0 },
      )
      // #372: у приёма пищи нет поля клетчатки — она хранится только в дне
      // (DiaryEntry.fiber). Раньше здесь суммировалось несуществующее m.fiber,
      // поэтому на главном экране у всех FatSecret-клиентов было «Кл 0».
      return { ...sum, fiber: diary?.fiber ?? 0 }
    }
    return {
      calories: diary?.calories ?? 0,
      protein: diary?.protein ?? 0,
      fat: diary?.fat ?? 0,
      carbs: diary?.carbs ?? 0,
      fiber: diary?.fiber ?? 0,
    }
  }, [meals, diary])

  // goal === null → куратор ещё не задал норму. Не показываем фейковый дефолт
  // (раньше всегда 2200), а явно сообщаем что нормы нет.
  const hasGoal = !!goal && (goal.calories ?? 0) > 0
  const targetKcal = goal?.calories ?? 2200
  const targetP = goal?.protein ?? 150
  const targetF = goal?.fat ?? 70
  const targetC = goal?.carbs ?? 250
  // Норма клетчатки от куратора; если не задана — дефолт 25 г для шкалы.
  const targetFiber = goal?.fiber ?? 25
  const kcalPct = hasGoal && targetKcal > 0 ? Math.min(100, (totals.calories / targetKcal) * 100) : 0
  const filled = totals.calories > 0

  const macros = [
    { label: 'Б', value: totals.protein, max: targetP, color: C.protein },
    { label: 'Ж', value: totals.fat, max: targetF, color: C.fat },
    { label: 'У', value: totals.carbs, max: targetC, color: C.carbs },
    { label: 'Кл', value: totals.fiber, max: targetFiber, color: C.fiber },
  ]

  return (
    <div
      data-testid="compact-nutrition-widget"
      data-state={filled ? 'submitted' : 'empty'}
      style={{
        borderRadius: 16,
        padding: 12,
        // #1: заливка как у блока «Отчёты» (единый вид) — цвет несёт только рамка
        // (зелёная = сдано, нейтральная = ещё не заполнено).
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: filled
          ? '1.5px solid rgba(52,199,89,0.55)'
          : '1px solid rgba(160,174,192,0.32)',
        boxShadow: '0 1px 2px rgba(26,58,92,0.05)',
        fontFamily: F,
        transition: 'background 0.25s, border-color 0.25s, box-shadow 0.25s',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{
          position: 'relative',
          width: 32, height: 32, borderRadius: 10,
          background: filled ? 'rgba(52,199,89,0.16)' : 'rgba(108,92,231,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.25s',
        }}>
          {filled
            ? <CheckCircle size={18} color="#1C9E44" />
            : <Utensils size={16} color="#6C5CE7" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.1 }}>Питание</span>
            {filled && (
              // Сдан отчёт: зелёный фон карточки + галочка (слева) уже сообщают
              // статус — здесь только награда, без слова «Сдано».
              <span
                data-testid="nutrition-submitted-chip"
                style={{
                  fontSize: 10, fontWeight: 800,
                  color: '#1C9E44', lineHeight: 1,
                }}
              >
                +10 ХР
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: filled ? '#1C9E44' : C.tertiary, marginTop: 2, fontWeight: filled ? 600 : 400 }}>
            {hasGoal
              ? `${Math.round(totals.calories).toLocaleString('ru-RU')} / ${targetKcal.toLocaleString('ru-RU')} ккал`
              : (filled
                  ? `${Math.round(totals.calories).toLocaleString('ru-RU')} ккал · ${onEditGoal ? 'норма не задана' : 'норму задаёт куратор'}`
                  : (onEditGoal ? 'Задай свою норму' : 'Куратор ещё не задал норму'))}
          </div>
        </div>
        {/* FatSecret — только иконка: зелёная (подключён) / серая (нет, тап = подключить).
            #546 (три тарифа): на тарифе без синхронизации иконка остаётся, но
            выключена — тап объясняет, что подтягивалось бы само. 'off' — иконки нет. */}
        {fatSecretState === 'off' ? null : fatSecretState === 'gray' ? (
          <button
            data-testid="fatsecret-gray-badge"
            onClick={(e) => { e.stopPropagation(); toast('Синхронизация с FatSecret — в полной программе: питание подтягивалось бы само каждый час, без ручного ввода.') }}
            aria-disabled="true"
            aria-label="Синхронизация с FatSecret недоступна на тарифе"
            title="Только в полной программе"
            style={{
              width: 28, height: 28, borderRadius: 10, flexShrink: 0,
              border: '1px dashed rgba(26,58,92,0.25)', background: 'rgba(26,58,92,0.03)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'not-allowed', opacity: 0.6,
            }}
          >
            <FatSecretIcon size={16} color="#B0B8C4" />
          </button>
        ) : fatSecretConnected ? (
          <div
            data-testid="fatsecret-connected-badge"
            title="Питание синхронизируется из FatSecret"
            style={{
              width: 28, height: 28, borderRadius: 10, flexShrink: 0,
              background: 'rgba(92,168,61,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FatSecretIcon size={16} color="#1FA838" />
          </div>
        ) : (
          <button
            data-testid="connect-fatsecret-btn"
            onClick={(e) => { e.stopPropagation(); onConnectFS() }}
            aria-label="Подключить FatSecret"
            title="Подключить FatSecret"
            style={{
              width: 28, height: 28, borderRadius: 10, flexShrink: 0,
              border: 'none', background: 'rgba(26,58,92,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <FatSecretIcon size={16} color="#9AA5B4" />
          </button>
        )}
        {/* Комментарии куратора — только иконка, с точкой при непросмотренных. */}
        {onOpenCommentsHistory && (
          <button
            data-testid="nutrition-comments-icon"
            onClick={(e) => { e.stopPropagation(); onOpenCommentsHistory() }}
            aria-label="Комментарии куратора"
            title="Комментарии куратора"
            style={{
              position: 'relative',
              width: 28, height: 28, borderRadius: 10, flexShrink: 0,
              border: 'none', background: 'rgba(108,92,231,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <MessageSquare size={15} color="#6C5CE7" />
            {hasUnreadComment && (
              <span
                data-testid="nutrition-unread-dot"
                style={{
                  position: 'absolute', top: -2, right: -2,
                  width: 9, height: 9, borderRadius: '50%',
                  background: '#FF3B30', border: '2px solid white',
                  boxShadow: '0 0 5px rgba(255,59,48,0.55)',
                }}
              />
            )}
          </button>
        )}
        <button
          onClick={() => setExpanded(v => !v)}
          aria-label={expanded ? 'Свернуть' : 'Раскрыть'}
          style={{
            width: 28, height: 28, borderRadius: 10,
            border: 'none', background: 'rgba(26,58,92,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          <ChevronDown size={14} color={C.text} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
      </div>

      {/* Compact macro bars */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: C.kcal, lineHeight: 1 }}>К</span>
          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(26,58,92,0.07)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${kcalPct}%`, background: C.kcal, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
        {macros.map((m) => {
          const pct = m.max > 0 ? Math.min(100, (m.value / m.max) * 100) : 0
          return (
            <div key={m.label} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: m.color, lineHeight: 1 }}>{m.label}</span>
              <div style={{ flex: 1, height: 5, borderRadius: 2.5, background: 'rgba(26,58,92,0.07)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: m.color, borderRadius: 2.5, transition: 'width 0.3s' }} />
              </div>
            </div>
          )
        })}
      </div>

      {/* По каждому макросу: поставлено (синхронизировано сегодня) / выставленная
          норма куратора — без слова «Норма», числа говорят сами за себя. */}
      {hasGoal && (
        <div style={{ marginTop: 6, fontSize: 10.5, color: C.tertiary, lineHeight: 1.4, display: 'flex', flexWrap: 'wrap', gap: '0 10px', alignItems: 'center' }}>
          {macros.filter((m) => m.max > 0).map((m) => (
            <span key={m.label}>
              <span style={{ color: m.color, fontWeight: 700 }}>{m.label}</span> {Math.round(m.value)}/{m.max}г
            </span>
          ))}
          {goal?.curatorName && <span style={{ opacity: 0.7 }}>· {goal.curatorName}</span>}
        </div>
      )}

      {/* Quick CTA when empty + collapsed.
          При подключённом FatSecret в данные подгружаются автоматически — ручной ввод
          скрыт. Если FS подключён и данных нет → подсказка «Заполни питание в FatSecret»
          (тап не открывает sheet, только информирует). */}
      {/* Свёрнуто и пусто. При ФС-подключении свёрнутое состояние — это просто
          сжатие (КБЖУ/Норма), без подсказок «заполни питание»: они в раскрытом
          блоке. Поэтому компактный CTA показываем ТОЛЬКО без ФС. */}
      {!filled && !expanded && !fatSecretConnected && (
        <button
          onClick={onOpenManual}
          style={{
            marginTop: 10,
            width: '100%', height: 36, borderRadius: 10,
            border: '1.5px dashed #C4CDD5',
            background: 'rgba(255,255,255,0.5)',
            color: '#6C5CE7', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: F,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Plus size={14} /> Внести КБЖУ за день
        </button>
      )}

      {/* Expanded detail */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ paddingTop: 12 }}>
              {/* #262: инлайн-тред комментариев по питанию убран из карточки —
                  комментарии и ответ куратору теперь во всплывашке (иконка 💬
                  сверху карточки → NutritionCommentsHistorySheet с полем ответа). */}

              {/* #546: при «серым» блок синхронизации с активной кнопкой — ложь; показываем ручной ввод. */}
              {fatSecretConnected && fatSecretState === 'open' ? (
                <>
                {meals.length > 0 ? (
                  // Просмотр приёмов пищи из FatSecret. Ручное редактирование
                  // отключено — источник данных FS.
                  <>
                    <NutritionCarousel meals={meals} goal={goal} onOpen={onOpenMeal} />
                    {/* Атрибуция обязательна по Attribution Policy FatSecret —
                        показываем под данными, которые пришли из их API. */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                      <FatSecretAttribution />
                    </div>
                  </>
                ) : fsHintHidden ? (
                  // #269: свёрнуто — подсказка скрыта (с памятью), обновление остаётся.
                  <div
                    data-testid="fatsecret-hint-collapsed"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, fontFamily: F }}
                  >
                    <button
                      type="button"
                      data-testid="fatsecret-hint-show"
                      onClick={(e) => { e.stopPropagation(); toggleFsHint(false) }}
                      style={{
                        background: 'transparent', border: 'none', padding: '4px 6px', cursor: 'pointer',
                        color: '#5C7B4A', fontSize: 12, fontWeight: 600, fontFamily: F,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      <FatSecretIcon size={14} /> Подсказка FatSecret
                    </button>
                    {fsSyncBtn}
                  </div>
                ) : (
                  <div
                    data-testid="fatsecret-empty-hint-expanded"
                    style={{
                      width: '100%',
                      border: '1.5px dashed rgba(92,168,61,0.45)',
                      background: 'rgba(92,168,61,0.06)',
                      borderRadius: 14, padding: '14px 12px',
                      display: 'flex', alignItems: 'center', gap: 10,
                      fontFamily: F,
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 11,
                      background: 'rgba(92,168,61,0.16)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <FatSecretIcon size={20} />
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#3F7E2A' }}>
                        Заполни питание в FatSecret
                      </div>
                      <div style={{ fontSize: 11, color: '#5C7B4A', marginTop: 2, lineHeight: 1.4 }}>
                        Подтянутся сами каждый час, итог дня — в 23:59; или обнови кнопкой.{hasGoal ? ` Цель: ${targetKcal.toLocaleString('ru-RU')} ккал` : (onEditGoal ? ' Норму задаёшь сам — в «Моей норме».' : ' Норму задаёт куратор.')}
                      </div>
                    </div>
                    {fsSyncBtn}
                    <button
                      type="button"
                      data-testid="fatsecret-hint-hide"
                      onClick={(e) => { e.stopPropagation(); toggleFsHint(true) }}
                      aria-label="Скрыть подсказку"
                      title="Скрыть подсказку"
                      style={{
                        flexShrink: 0, width: 26, height: 26, borderRadius: 8, padding: 0, border: 'none',
                        background: 'transparent', color: '#8CA378', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <X size={15} />
                    </button>
                  </div>
                )}
                {/* Ручной ввод при ФС — дневное КБЖУ (стандартный формат, как у не-ФС).
                    Перезапишется синком только если за этот день появятся данные ФС. */}
                <button
                  data-testid="fatsecret-manual-fill-btn-expanded"
                  onClick={(e) => { e.stopPropagation(); onOpenManual() }}
                  style={{
                    width: '100%', height: 38, marginTop: 8, borderRadius: 12, cursor: 'pointer',
                    border: '1px solid rgba(26,58,92,0.14)', background: 'rgba(255,255,255,0.6)',
                    color: C.text, fontSize: 13, fontWeight: 600, fontFamily: F,
                  }}
                >
                  Заполнить вручную
                </button>
                </>
              ) : meals.length > 0 ? (
                <NutritionCarousel meals={meals} goal={goal} onOpen={onOpenMeal} />
              ) : (
                <button
                  data-testid="day-nutrition-card"
                  onClick={onOpenManual}
                  style={{
                    width: '100%',
                    border: '1.5px dashed #C4CDD5',
                    background: 'rgba(255,255,255,0.5)',
                    borderRadius: 14,
                    padding: '14px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    fontFamily: F,
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(108,92,231,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Pencil size={14} color="#6C5CE7" />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                      {filled ? 'Изменить КБЖУ за день' : 'Внести КБЖУ вручную'}
                    </div>
                    <div style={{ fontSize: 11, color: C.tertiary, marginTop: 2 }}>
                      {hasGoal ? `Цель: ${targetKcal.toLocaleString('ru-RU')} ккал` : (onEditGoal ? 'Норму задаёшь сам' : 'Норму задаёт куратор')}
                    </div>
                  </div>
                </button>
              )}

              {/* #537: своя норма — тарифы без куратора задают её сами. */}
              {onEditGoal && (
                <button
                  data-testid="nutrition-goal-open"
                  onClick={onEditGoal}
                  style={{
                    width: '100%', marginTop: 8, borderRadius: 12, padding: '10px 12px',
                    border: '1px solid rgba(52,199,89,0.28)', background: 'rgba(52,199,89,0.08)',
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: F,
                    fontSize: 13, fontWeight: 600, color: '#1B5E20', textAlign: 'left',
                  }}
                >
                  <Target size={14} color="#1B9E44" />
                  {hasGoal ? 'Изменить свою норму' : 'Задать свою норму'}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Unified day/week reports block — combines daily checks + weekly reports ──
function ReportsBlock({
  diary,
  diaryCache,
  strengthByDate,
  cardioByDate,
  lectureCountByDate,
  measurementsByDate,
  photosByDate,
  selectedDate,
  workoutDraftDate = null,
  measWindowOpen = false,
  measWeekDone = false,
  photoWindowOpen = false,
  programDaysCount = null,
  periodicEnabled = true,
  photosEnabled = true,
  metricsEnabled = true,
  gamificationOn = true,
  techniqueByDate,
  techniqueEarnedByDate,
  xpEarnedByDate,
  onOpenMetrics,
  onOpenNutrition,
  onOpenTraining,
  onOpenLectures,
  onOpenMeasurements,
  onOpenPhotos,
  onOpenAnalytics,
  hasUnreadMetricsComment = false,
  hasMetricsComment = false,
  onOpenMetricsComment,
}: {
  diary: DiaryEntryData | null
  diaryCache: Record<string, DiaryEntryData | undefined>
  /** Даты с силовой тренировкой (есть упражнения) — пункт «Тренировка» +10 XP. */
  strengthByDate: Set<string>
  /** Даты с отдельной кардио-активностью — пункт «Кардио» +5 XP. */
  cardioByDate: Set<string>
  /** Кол-во просмотренных лекций по датам — пункт «Лекция» +1 XP (#205). */
  lectureCountByDate?: Map<string, number>
  measurementsByDate?: Set<string>
  photosByDate?: Set<string>
  selectedDate: Date
  /** Дата (YYYY-MM-DD) начатой, но не завершённой тренировки — драфт WorkoutActive. */
  workoutDraftDate?: string | null
  /** Окно еженедельных замеров открыто и замер ещё не сдан. */
  measWindowOpen?: boolean
  measWeekDone?: boolean
  /** Окно фото прогресса (раз в 30 дней) открыто и фото ещё не сдано. */
  photoWindowOpen?: boolean
  /** Дней в активной программе тренировок — недельный лимит тренировок. */
  programDaysCount?: number | null
  /** Периодические отчёты (замеры/фото) запрашиваются у клиента (не Клуб). */
  periodicEnabled?: boolean
  /**
   * #610: фото прогресса есть в тарифе (матрица, функция `photos`). У Программы
   * его нет — сервер фото не принимает, а плитка «Фото» в шкале всё равно
   * предлагала их сдать и открывала шторку «Сдать фото в Telegram».
   */
  photosEnabled?: boolean
  /** Три тарифа: плитки сна/шагов/веса — по функции `metrics`. */
  metricsEnabled?: boolean
  /** Три тарифа: без геймификации XP не показываем. */
  gamificationOn?: boolean
  /** Даты сдачи видео техники (#234) — пункт «Техника» в отчёте за эти дни. */
  techniqueByDate?: Set<string>
  /** Даты, где техника принесла XP (в пределах недельного лимита = дни программы). */
  techniqueEarnedByDate?: Set<string>
  /**
   * #507: день → типы отчётов, которые в этот день РЕАЛЬНО принесли XP.
   * undefined = данных ещё нет (или старый бэк) → считаем по номиналу, как
   * раньше, чтобы не занижать шкалу на ровном месте.
   */
  xpEarnedByDate?: Map<string, Set<string>>
  onOpenMetrics: () => void
  onOpenNutrition: () => void
  onOpenTraining: () => void
  onOpenLectures?: () => void
  onOpenMeasurements?: () => void
  onOpenPhotos?: () => void
  onOpenAnalytics?: () => void
  hasUnreadMetricsComment?: boolean
  hasMetricsComment?: boolean
  onOpenMetricsComment?: () => void
}) {
  const [period, setPeriod] = useState<'day' | 'week'>('day')

  type ReportItem = {
    key: string
    label: string
    Icon: typeof Dumbbell
    color: string
    xp: number
    xpMax: number
    done: boolean
    value?: string
    onClick?: () => void
  }

  // #507: принёс ли отчёт этого типа XP в конкретный день. Пока карты нет
  // (первый рендер/старый бэк) — считаем по-старому, по факту записи.
  const earnedXpOn = (dayKey: string, actionType: string): boolean =>
    !xpEarnedByDate || (xpEarnedByDate.get(dayKey)?.has(actionType) ?? false)

  const dayItems: ReportItem[] = useMemo(() => {
    const selectedKey = toDateKey(selectedDate)
    const isToday = selectedKey === toDateKey(new Date())
    // «Тренировка» = силовая (есть упражнения) или легаси-текстовая. Кардио —
    // отдельный пункт ниже, чтобы не показываться как силовая +10 XP.
    const hasTraining = strengthByDate.has(selectedKey) || !!diary?.training
    const hasCardio = cardioByDate.has(selectedKey)
    const trainingStarted = workoutDraftDate === selectedKey
    const hasNutrition = (diary?.meals?.length ?? 0) > 0 || diary?.calories != null
    const hasSleep = diary?.sleepQuality != null || diary?.sleep != null
    const hasSteps = diary?.steps != null && diary.steps > 0
    const hasWeight = diary?.weight != null
    // Слово вместо emoji — компактный лейбл качества сна.
    const sleepLabel = diary?.sleepQuality === 'GOOD'
      ? 'Выспался'
      : diary?.sleepQuality === 'SO_SO'
        ? 'Недоспал'
        : diary?.sleepQuality === 'BAD'
          ? 'Не выспался'
          : (diary?.sleep != null ? `${diary.sleep} ч` : '—')

    const items: ReportItem[] = []
    // Тренировка — НЕ ежедневный отчёт: пункт (+10 XP, день 13 → 23) появляется
    // только когда тренировка в этот день записана или начата (драфт). Дни без
    // тренировки «пустые» — базовые 13 XP закрываются ежедневными отчётами.
    if (hasTraining || trainingStarted) {
      // #521: та же логика, что у кардио — силовая сверх недельного лимита
      // (дни программы) засчитывается, но XP не приносит.
      const trainPaid = hasTraining && earnedXpOn(selectedKey, 'TRAINING_REPORT')
      items.push({
        key: 'train', label: 'Тренировка', Icon: Dumbbell, color: '#4A6CF7',
        xp: trainPaid ? 10 : 0, xpMax: 10, done: hasTraining,
        value: hasTraining ? (trainPaid ? 'сдана' : 'сдана · лимит недели') : 'начата',
        onClick: onOpenTraining,
      })
    }
    // Кардио-активность (+5 XP) — пункт появляется только в день, когда кардио сдано.
    // #521: XP показываем ТОЛЬКО если начисление реально было. Лимит кардио —
    // 2 в неделю, и третья активность засчитывается, но баллов не приносит.
    // Раньше день всегда рисовал «+5», и шкала обещала больше, чем в рейтинге:
    // у клиентки 22.08 приложение показывало 18 XP против 13 начисленных.
    if (hasCardio) {
      const cardioPaid = earnedXpOn(selectedKey, 'TRAINING_CARDIO')
      items.push({
        key: 'cardio', label: 'Кардио', Icon: Flame, color: '#FF6B6B',
        xp: cardioPaid ? 5 : 0, xpMax: 5, done: true,
        value: cardioPaid ? 'сдано' : 'сдано · лимит недели',
        onClick: onOpenTraining,
      })
    }
    // Техника упражнений (+5 XP, #234) — отдельное «запористое» действие: клиент
    // отправил видео техники куратору. Пункт появляется в день, который ПРИНЁС
    // XP (в пределах недельного лимита = дни программы); сверх лимита техника
    // засчитана, но XP нет — в шкале XP такой день не показываем.
    if (techniqueEarnedByDate?.has(selectedKey)) {
      items.push({ key: 'technique', label: 'Техника', Icon: Video, color: '#14B8A6', xp: 5, xpMax: 5, done: true, value: 'сдана', onClick: onOpenTraining })
    }
    // #205: «Лекция» (+1 XP) — появляется только в день просмотра (как кардио),
    // до просмотра пункта/«плюсика» нет.
    const lecturesThisDay = lectureCountByDate?.get(selectedKey) ?? 0
    if (lecturesThisDay > 0) {
      items.push({ key: 'lecture', label: 'Лекция', Icon: GraduationCap, color: '#6C5CE7', xp: lecturesThisDay, xpMax: lecturesThisDay, done: true, value: lecturesThisDay > 1 ? `×${lecturesThisDay}` : 'просмотрена', onClick: onOpenLectures })
    }
    items.push(
      { key: 'food', label: 'Питание', Icon: Utensils, color: '#F59E0B', xp: hasNutrition && earnedXpOn(selectedKey, 'NUTRITION_REPORT') ? 10 : 0, xpMax: 10, done: hasNutrition, value: diary?.calories != null ? `${Math.round(diary.calories)} ккал` : '—', onClick: onOpenNutrition },
    )
    // Три тарифа: у Программы отчётов по сну, шагам и весу нет.
    if (metricsEnabled) items.push(
      { key: 'sleep', label: 'Сон', Icon: Moon, color: '#8B7FF5', xp: hasSleep && earnedXpOn(selectedKey, 'SLEEP_LOG') ? 1 : 0, xpMax: 1, done: hasSleep, value: sleepLabel, onClick: onOpenMetrics },
      { key: 'steps', label: 'Шаги', Icon: SneakerIcon, color: '#4A9FE5', xp: hasSteps && earnedXpOn(selectedKey, 'STEPS_LOG') ? 1 : 0, xpMax: 1, done: hasSteps, value: diary?.steps != null ? diary.steps.toLocaleString('ru-RU') : '—', onClick: onOpenMetrics },
      { key: 'weight', label: 'Вес', Icon: Scale, color: '#34C759', xp: hasWeight && earnedXpOn(selectedKey, 'WEIGHT_LOG') ? 1 : 0, xpMax: 1, done: hasWeight, value: formatWeight(diary?.weight) != null ? `${formatWeight(diary?.weight)} кг` : '—', onClick: onOpenMetrics },
    )
    // Периодические отчёты появляются в дне только когда запрашиваются:
    // в день сдачи — выполненным пунктом, сегодня при открытом окне — ожиданием.
    // После сдачи в остальные дни окна не отображаются.
    if (periodicEnabled) {
      // #3: «сдано» берём из недельного цикла (как карточка «Замеры тела» и её
      // шторка), а не из факта сдачи в конкретный день — иначе плитка висела синей
      // «сдать» с плюсиком, хотя замеры за неделю уже сданы.
      // #356: но в XP-шкалу ДНЯ замеры идут только если сданы ИМЕННО в этот день —
      // иначе каждый день недели показывал «+10 сегодня», будто сдал сейчас.
      // Сданы в другой день недели → плитка «сданы» есть, но из сумм дня выключена
      // (xp/xpMax = 0).
      const measThisDay = measurementsByDate?.has(selectedKey) ?? false
      const measDone = measWeekDone || measThisDay
      if (measDone || (isToday && measWindowOpen)) {
        const inDayScale = measThisDay || !measDone
        items.push({ key: 'meas', label: 'Замеры', Icon: Ruler, color: '#4A6CF7', xp: measThisDay ? 10 : 0, xpMax: inDayScale ? 10 : 0, done: measDone, value: measDone ? 'сданы' : '—', onClick: onOpenMeasurements })
      }
      // #205: фото в ДНЕВНОЙ шкале — только в день сдачи (плашка «сдано»), без
      // «плюсика»-ожидания. Добавить фото можно из недельной шкалы (там «+»
      // при открытом окне). В неделе поведение прежнее.
      const photoDoneThisDay = photosByDate?.has(selectedKey) ?? false
      if (photosEnabled && photoDoneThisDay) {
        items.push({ key: 'photo', label: 'Фото', Icon: Camera, color: '#8B7FF5', xp: 10, xpMax: 10, done: true, value: 'сдано', onClick: onOpenPhotos })
      }
    }
    return items
  }, [diary, selectedDate, strengthByDate, cardioByDate, lectureCountByDate, workoutDraftDate, measurementsByDate, photosByDate, measWindowOpen, measWeekDone, photoWindowOpen, periodicEnabled, photosEnabled, techniqueEarnedByDate, xpEarnedByDate, onOpenMetrics, onOpenNutrition, onOpenTraining, onOpenLectures, onOpenMeasurements, onOpenPhotos])

  const weekItems: ReportItem[] = useMemo(() => {
    const dow = (selectedDate.getDay() + 6) % 7
    const monday = new Date(selectedDate)
    monday.setDate(monday.getDate() - dow)
    monday.setHours(0, 0, 0, 0)
    const todayKey = toDateKey(new Date())
    let weekHasToday = false
    let train = 0, cardio = 0, food = 0, sleep = 0, steps = 0, weight = 0
    let meas = 0, photo = 0, lectures = 0, techSubmitted = 0, techEarned = 0
    // #507: отдельно — сколько дней РЕАЛЬНО принесли XP (бэкфилл и правки
    // задним числом пишут дневник без начисления).
    let foodXp = 0, sleepXp = 0, stepsXp = 0, weightXp = 0
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday)
      d.setDate(d.getDate() + i)
      const k = toDateKey(d)
      if (k === todayKey) weekHasToday = true
      const e = diaryCache[k]
      if (strengthByDate.has(k) || e?.training) train++
      if (cardioByDate.has(k)) cardio++
      if ((e?.meals?.length ?? 0) > 0 || e?.calories != null) { food++; if (earnedXpOn(k, 'NUTRITION_REPORT')) foodXp++ }
      if (e?.sleepQuality != null || e?.sleep != null) { sleep++; if (earnedXpOn(k, 'SLEEP_LOG')) sleepXp++ }
      if (e?.steps != null && e.steps > 0) { steps++; if (earnedXpOn(k, 'STEPS_LOG')) stepsXp++ }
      if (e?.weight != null) { weight++; if (earnedXpOn(k, 'WEIGHT_LOG')) weightXp++ }
      if (measurementsByDate?.has(k)) meas++
      if (photosByDate?.has(k)) photo++
      if (techniqueByDate?.has(k)) techSubmitted++
      if (techniqueEarnedByDate?.has(k)) techEarned++
      lectures += lectureCountByDate?.get(k) ?? 0
    }
    // Недельный лимит тренировок = дни активной программы (фолбэк — общие 3).
    const trainLimit = Math.max(1, programDaysCount ?? 3)
    const measDone = meas > 0 // 1 раз в неделю достаточно
    // Кардио: до 2 активностей в неделю по +5 XP (XP_REWARDS.TRAINING_CARDIO).
    const cardioLimit = 2
    const cardioCapped = Math.min(cardio, cardioLimit)
    const items: ReportItem[] = [
      { key: 'train', label: 'Тренировка', Icon: Dumbbell, color: '#4A6CF7', xp: Math.min(train, trainLimit) * 10, xpMax: trainLimit * 10, done: train >= trainLimit, value: `${Math.min(train, trainLimit)}/${trainLimit}`, onClick: onOpenTraining },
      { key: 'food', label: 'Питание', Icon: Utensils, color: '#F59E0B', xp: foodXp * 10, xpMax: 70, done: food >= 7, value: `${food}/7`, onClick: onOpenNutrition },
      { key: 'sleep', label: 'Сон', Icon: Moon, color: '#8B7FF5', xp: sleepXp, xpMax: 7, done: sleep >= 7, value: `${sleep}/7`, onClick: onOpenMetrics },
      { key: 'steps', label: 'Шаги', Icon: SneakerIcon, color: '#4A9FE5', xp: stepsXp, xpMax: 7, done: steps >= 7, value: `${steps}/7`, onClick: onOpenMetrics },
      { key: 'weight', label: 'Вес', Icon: Scale, color: '#34C759', xp: weightXp, xpMax: 7, done: weight >= 7, value: `${weight}/7`, onClick: onOpenMetrics },
    ]
    // Кардио появляется в неделе только когда хотя бы одна активность сдана.
    if (cardio > 0) {
      items.push({ key: 'cardio', label: 'Кардио', Icon: Flame, color: '#FF6B6B', xp: cardioCapped * 5, xpMax: cardioLimit * 5, done: cardio >= cardioLimit, value: `${cardioCapped}/${cardioLimit}`, onClick: onOpenTraining })
    }
    // Техника (#234): недельный лимит = дни программы (trainLimit). Показываем
    // earned/N — сколько сдач принесло XP из лимита. Пункт появляется, как
    // только техника хоть раз сдана на неделе.
    if (techSubmitted > 0) {
      items.push({ key: 'technique', label: 'Техника', Icon: Video, color: '#14B8A6', xp: Math.min(techEarned, trainLimit) * 5, xpMax: trainLimit * 5, done: techEarned >= trainLimit, value: `${Math.min(techEarned, trainLimit)}/${trainLimit}`, onClick: onOpenTraining })
    }
    // #205: «Лекция» в неделе — появляется по факту просмотров (счёт за неделю).
    if (lectures > 0) {
      items.push({ key: 'lecture', label: 'Лекция', Icon: GraduationCap, color: '#6C5CE7', xp: lectures, xpMax: lectures, done: true, value: `${lectures}`, onClick: onOpenLectures })
    }
    if (periodicEnabled) {
      // Замеры запрашиваются каждую неделю; фото — раз в 30 дней, поэтому пункт
      // появляется в неделе только когда фото сдано на этой неделе или окно
      // открыто прямо сейчас (текущая неделя).
      items.push({ key: 'meas', label: 'Замеры', Icon: Ruler, color: '#4A6CF7', xp: measDone ? 10 : 0, xpMax: 10, done: measDone, value: `${Math.min(meas, 1)}/1`, onClick: onOpenMeasurements })
      if (photosEnabled && (photo > 0 || (weekHasToday && photoWindowOpen))) {
        items.push({ key: 'photo', label: 'Фото', Icon: Camera, color: '#8B7FF5', xp: photo > 0 ? 10 : 0, xpMax: 10, done: photo > 0, value: `${Math.min(photo, 1)}/1`, onClick: onOpenPhotos })
      }
    }
    return items
  }, [selectedDate, diaryCache, strengthByDate, cardioByDate, lectureCountByDate, measurementsByDate, photosByDate, photoWindowOpen, programDaysCount, periodicEnabled, photosEnabled, techniqueByDate, techniqueEarnedByDate, xpEarnedByDate, onOpenMetrics, onOpenNutrition, onOpenTraining, onOpenLectures, onOpenMeasurements, onOpenPhotos])

  const items = period === 'day' ? dayItems : weekItems
  const earned = items.reduce((s, i) => s + i.xp, 0)
  const total = items.reduce((s, i) => s + i.xpMax, 0)
  const pct = total > 0 ? (earned / total) * 100 : 0
  const pendingCount = items.filter((i) => !i.done).length

  return (
    <div style={{
      borderRadius: 16,
      padding: '12px 12px 10px',
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.4)',
      fontFamily: F,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Отчёты</span>
          {hasUnreadMetricsComment ? (
            <button
              data-testid="metrics-unread-comment-badge"
              onClick={(e) => { e.stopPropagation(); onOpenMetricsComment?.() }}
              style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
              title="Новый комментарий куратора по показателям"
            >
              <UnreadCommentBadge size={20} />
            </button>
          ) : hasMetricsComment ? (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenMetricsComment?.() }}
              style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'inline-flex', color: '#9AA5B4' }}
              title="Комментарий куратора по показателям"
            >
              <MessageSquare size={15} />
            </button>
          ) : null}
          {pendingCount > 0 && (
            <span
              data-testid="reports-pending-badge"
              aria-label={`${pendingCount} не заполнено`}
              style={{
                minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                background: '#FF3B30',
                color: 'white',
                fontSize: 10, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
                boxShadow: '0 2px 6px rgba(255,59,48,0.30)',
              }}
            >
              {pendingCount}
            </span>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {onOpenAnalytics && (
          <button
            data-testid="reports-analytics-icon"
            onClick={onOpenAnalytics}
            aria-label="Аналитика"
            style={{
              width: 26, height: 26, borderRadius: 8,
              border: 'none', background: 'rgba(108,99,255,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <BarChart3 size={14} color="#6C63FF" />
          </button>
        )}
        <div style={{ display: 'flex', gap: 0, padding: 2, borderRadius: 10, background: 'rgba(26,58,92,0.06)' }}>
          {(['day', 'week'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                height: 22,
                padding: '0 10px',
                borderRadius: 8,
                border: 'none',
                background: period === p ? 'white' : 'transparent',
                color: period === p ? C.text : C.tertiary,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: F,
                boxShadow: period === p ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {p === 'day' ? 'День' : 'Неделя'}
            </button>
          ))}
        </div>
      </div>

      {/* progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(26,58,92,0.07)', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 4,
            background: 'linear-gradient(90deg, #4A6CF7, #6C5CE7)',
            transition: 'width 0.35s ease',
          }} />
        </div>
        {gamificationOn && (
          <span style={{ fontSize: 11, fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>
            {earned} / {total} XP
          </span>
        )}
      </div>

      {/* compact tile grid — all 7 reports visible without scroll */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${items.length}, 1fr)`,
          gap: 6,
        }}
      >
        {items.map(it => {
          const Icon = it.Icon
          return (
            <div
              key={it.key}
              data-testid={`day-task-${it.key}`}
              data-done={it.done ? 'true' : 'false'}
              onClick={it.onClick}
              className={it.done ? undefined : 'report-tile-pending'}
              title={gamificationOn ? `${it.label} · +${it.xpMax} XP${it.value ? ` · ${it.value}` : ''}` : `${it.label}${it.value ? ` · ${it.value}` : ''}`}
              style={{
                borderRadius: 9,
                padding: '6px 2px 5px',
                // #273: разноцветные заливки плиток «намешаны» (питание выделялось
                // сильнее прочих). Оставляем только обводку — заливку убрали, тон
                // задаёт цветная рамка + иконка, вес плиток одинаковый.
                background: 'transparent',
                border: `1px ${it.done ? 'solid' : 'dashed'} ${it.done ? `${it.color}55` : 'rgba(160,174,192,0.28)'}`,
                cursor: it.onClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
                minWidth: 0,
              }}
            >
              {it.done ? (
                <div style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#34C759',
                }} />
              ) : (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', top: -4, right: -4,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#6C5CE7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(108,92,231,0.40), 0 0 0 1.5px white',
                  }}
                >
                  <Plus size={10} color="white" strokeWidth={3.2} />
                </div>
              )}
              <div style={{
                width: 24, height: 24, borderRadius: 7,
                // #273: нейтральный чип (единый для всех) — цвет несёт рамка + глиф.
                background: 'rgba(160,174,192,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={13} color={it.color} />
              </div>
              <span style={{
                fontSize: 9,
                fontWeight: 600,
                color: it.done ? C.text : C.tertiary,
                textAlign: 'center',
                lineHeight: 1.1,
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 2px',
              }}>
                {it.label}
              </span>
              {period === 'week' && it.value && (
                <span style={{
                  fontSize: 10, fontWeight: 800,
                  color: it.done ? it.color : C.text,
                  lineHeight: 1,
                }}>
                  {it.value}
                </span>
              )}
              <span style={{ fontSize: 8, fontWeight: 700, color: it.done ? '#34C759' : C.tertiary, lineHeight: 1 }}>
                +{it.xpMax}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FuturePlaceholder({ plannedType }: { plannedType: 'strength' | 'cardio' | 'none' }) {
  const subtitle =
    plannedType === 'strength'
      ? 'Силовая тренировка запланирована'
      : plannedType === 'cardio'
        ? 'Кардио запланировано'
        : 'Нет запланированных активностей'

  return (
    <div
      style={{
        borderRadius: 20,
        padding: '28px 20px',
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.4)',
        textAlign: 'center',
        fontFamily: F,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'rgba(74,159,229,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px',
        }}
      >
        <SneakerIcon size={20} color="#4A9FE5" />
      </div>
      <p style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>Запланировано</p>
      <p style={{ fontSize: 13, color: C.tertiary, margin: 0 }}>{subtitle}</p>
    </div>
  )
}

// ---- Loading skeleton ----

function DiarySkeleton() {
  const pulse: CSSProperties = {
    borderRadius: 14,
    background: 'linear-gradient(90deg, rgba(26,58,92,0.04) 25%, rgba(26,58,92,0.08) 50%, rgba(26,58,92,0.04) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease-in-out infinite',
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', gap: 6 }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 68, ...pulse }} />
        ))}
      </div>
      <div style={{ height: 48, ...pulse }} />
      <div style={{ display: 'flex', gap: 8, overflow: 'hidden' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ minWidth: 148, height: 100, ...pulse }} />
        ))}
      </div>
      <div style={{ height: 120, ...pulse }} />
    </motion.div>
  )
}

// ---- Analytics mini-card ----

// ── Analytics mini-card with period selector + measurements tab ──
type AnalyticsTab = 'sleep' | 'steps' | 'weight' | 'measurements'
type AnalyticsPeriod = '1w' | '2w' | '1m' | '3m' | '6m'

const PERIOD_DAYS: Record<AnalyticsPeriod, number> = { '1w': 7, '2w': 14, '1m': 30, '3m': 90, '6m': 180 }
const PERIOD_LABEL: Record<AnalyticsPeriod, string> = { '1w': '7 дн', '2w': '2 нед', '1m': 'мес', '3m': '3 мес', '6m': '6 мес' }

// Body-part colors for measurements overlay
const MEAS_PARTS = [
  { key: 'arms',   label: 'Руки',   color: '#4A9FE5' },
  { key: 'chest',  label: 'Грудь',  color: '#6C63FF' },
  { key: 'waist',  label: 'Талия',  color: '#FF9500' },
  { key: 'belly',  label: 'Живот',  color: '#F97316' },
  { key: 'pelvis', label: 'Таз',    color: '#AF52DE' },
  { key: 'hips',   label: 'Бёдра',  color: '#EC4899' },
  { key: 'thigh',  label: 'Бедро',  color: '#FF2D55' },
  { key: 'calf',   label: 'Голень', color: '#24A1DE' },
  { key: 'neck',   label: 'Шея',    color: '#10B981' },
] as const

// Лёгкий бейдж изменения за период — для Diary mini-card (mode='relative' или 'absolute' для %).
function MiniPctBadge({
  curr, prev, periodLabel, goodWhen = 'up', mode = 'relative', suffix = '',
}: {
  curr: number | null
  prev: number | null
  periodLabel: string
  goodWhen?: 'up' | 'down' | 'neutral'
  mode?: 'relative' | 'absolute'
  suffix?: string
}) {
  if (curr == null || prev == null || (mode === 'relative' && prev === 0)) {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', fontFamily: F, padding: '2px 6px', borderRadius: 6, background: 'rgba(160,174,192,0.10)' }}>
        — за {periodLabel}
      </span>
    )
  }
  const raw = mode === 'absolute' ? (curr - prev) : ((curr - prev) / Math.abs(prev)) * 100
  const v = Math.abs(raw) < 0.05 ? 0 : Math.round(raw * 10) / 10
  let isGood: boolean | null = null
  if (goodWhen === 'up') isGood = v > 0
  else if (goodWhen === 'down') isGood = v < 0
  if (v === 0) isGood = null
  const color = isGood == null ? '#9CA3AF' : isGood ? '#16A34A' : '#DC2626'
  const bg = isGood == null ? 'rgba(160,174,192,0.10)' : isGood ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)'
  const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '•'
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color, fontFamily: F, padding: '2px 6px', borderRadius: 6, background: bg, whiteSpace: 'nowrap' }}>
      {arrow} {v > 0 ? '+' : ''}{v}{suffix || (mode === 'absolute' ? '' : '%')} <span style={{ opacity: 0.7, fontWeight: 600 }}>за {periodLabel}</span>
    </span>
  )
}

function AnalyticsMiniCard({
  diaryCache,
  measurementByDate,
  onOpenDetail,
}: {
  diaryCache: Record<string, DiaryEntryData | undefined>
  measurementByDate?: Record<string, Record<string, unknown>>
  onOpenDetail: () => void
}) {
  const [tab, setTab] = useState<AnalyticsTab>('sleep')
  const [period, setPeriod] = useState<AnalyticsPeriod>('1w')
  const [showSleepGuide, setShowSleepGuide] = useState(false)

  // Самая старая запись в кеше — для решения, какие периоды показывать.
  // Скрываем 3м/6м, если за этот срок нет ни одной записи (всё в кеше моложе порога).
  const oldestEntryAgeDays = useMemo<number>(() => {
    let oldestMs: number | null = null
    for (const key of Object.keys(diaryCache)) {
      const e = diaryCache[key]
      if (!e) continue
      const hasAny = (e.sleep != null && e.sleep > 0)
        || (e.steps != null && e.steps > 0)
        || (e.weight != null && e.weight > 0)
        || (e.calories != null && e.calories > 0)
        || !!e.training
      if (!hasAny) continue
      const t = new Date(e.date).getTime()
      if (oldestMs === null || t < oldestMs) oldestMs = t
    }
    if (measurementByDate) {
      for (const key of Object.keys(measurementByDate)) {
        const t = new Date(`${key}T00:00:00`).getTime()
        if (oldestMs === null || t < oldestMs) oldestMs = t
      }
    }
    if (oldestMs === null) return 0
    return Math.floor((Date.now() - oldestMs) / 86_400_000)
  }, [diaryCache, measurementByDate])

  const availablePeriods = useMemo<AnalyticsPeriod[]>(() => {
    const list: AnalyticsPeriod[] = ['1w', '2w', '1m']
    // 3 месяца — нужны данные старше 60 дней
    if (oldestEntryAgeDays >= 60) list.push('3m')
    // 6 месяцев — старше 120 дней
    if (oldestEntryAgeDays >= 120) list.push('6m')
    return list
  }, [oldestEntryAgeDays])

  // Если текущий период недоступен (например, кеш сузился), откатываемся к 7 дн.
  useEffect(() => {
    if (!availablePeriods.includes(period)) setPeriod('1w')
  }, [availablePeriods, period])

  const days = PERIOD_DAYS[period]

  const tabConfig = {
    sleep:  { Icon: Moon,  label: 'Сон',    unit: 'ч',  color: '#8B7FF5', formatter: (v: number) => v.toFixed(1) },
    steps:  { Icon: SneakerIcon,   label: 'Шаги',   unit: '',   color: '#4A9FE5', formatter: (v: number) => Math.round(v).toLocaleString('ru-RU') },
    weight: { Icon: Scale, label: 'Вес',    unit: 'кг', color: '#34C759', formatter: (v: number) => v.toFixed(1) },
    measurements: { Icon: Ruler, label: 'Замеры', unit: 'см', color: '#6C5CE7', formatter: (v: number) => v.toFixed(0) },
  } as const
  const cfg = tabConfig[tab]

  // #321: вес приходит из двух источников — дневник + «Замеры тела». Мёржим,
  // иначе у клиента, взвешивающегося через замеры, график/EMA/«С поправкой»
  // пустые и расходятся с «ежедневным весом». Тот же queryKey, что у основного
  // запроса замеров, — react-query переиспользует кэш.
  const { data: weightHistMeasurements } = useQuery({
    queryKey: ['diary', 'measurements'],
    queryFn: () => api.getMeasurements(),
    enabled: tab === 'weight',
  })
  const measurementWeightByDay = useMemo(() => {
    const map = new Map<string, number>()
    for (const m of ((weightHistMeasurements?.measurements ?? []) as Array<{ date: string; weight: number | null }>)) {
      const w = m.weight
      if (typeof w === 'number' && Number.isFinite(w) && w > 0 && w < 500) {
        map.set(labelDateKey(m.date), w)
      }
    }
    return map
  }, [weightHistMeasurements])

  // Single-metric series for sleep/steps/weight (числовые)
  const series = useMemo(() => {
    if (tab === 'measurements') return []
    const result: { value: number | null; date: Date }[] = []
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(TODAY)
      d.setDate(d.getDate() - i)
      const key = toDateKey(d)
      const entry = diaryCache[key]
      const raw = entry?.[tab]
      let value = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null
      // #321: день без дневного веса, но с весом из «Замеров» — берём замерный.
      if (value == null && tab === 'weight') value = measurementWeightByDay.get(key) ?? null
      result.push({ value, date: d })
    }
    return result
  }, [tab, days, diaryCache, measurementWeightByDay])

  // Sleep — отдельная серия по качеству (BAD/SO_SO/GOOD), legacy hours
  // конвертируем: ≥7ч → GOOD, ≥5.5ч → SO_SO, иначе BAD.
  type SleepQ = 'GOOD' | 'SO_SO' | 'BAD'
  const sleepSeries = useMemo(() => {
    if (tab !== 'sleep') return [] as Array<{ q: SleepQ | null; date: Date }>
    const out: Array<{ q: SleepQ | null; date: Date }> = []
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(TODAY)
      d.setDate(d.getDate() - i)
      const entry = diaryCache[toDateKey(d)]
      let q: SleepQ | null = null
      if (entry?.sleepQuality === 'GOOD' || entry?.sleepQuality === 'SO_SO' || entry?.sleepQuality === 'BAD') {
        q = entry.sleepQuality
      } else if (typeof entry?.sleep === 'number' && entry.sleep > 0) {
        q = entry.sleep >= 7 ? 'GOOD' : entry.sleep >= 5.5 ? 'SO_SO' : 'BAD'
      }
      out.push({ q, date: d })
    }
    return out
  }, [tab, days, diaryCache])
  const sleepCounts = useMemo(() => {
    let good = 0, soso = 0, bad = 0
    for (const s of sleepSeries) {
      if (s.q === 'GOOD') good++
      else if (s.q === 'SO_SO') soso++
      else if (s.q === 'BAD') bad++
    }
    return { good, soso, bad, total: good + soso + bad }
  }, [sleepSeries])
  const SLEEP_COLORS = { GOOD: '#34C759', SO_SO: '#FFCC00', BAD: '#FF3B30' } as const

  const validValues = series.map((s) => s.value).filter((v): v is number => v != null)
  const avg = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null

  // Серия за предыдущий период такой же длины — для %-изменения
  const prevSeriesValues = useMemo(() => {
    if (tab === 'measurements') return [] as number[]
    const out: number[] = []
    for (let i = days * 2 - 1; i >= days; i--) {
      const d = new Date(TODAY)
      d.setDate(d.getDate() - i)
      const entry = diaryCache[toDateKey(d)]
      const raw = entry?.[tab]
      if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) out.push(raw)
    }
    return out
  }, [tab, days, diaryCache])
  const prevAvg = prevSeriesValues.length > 0
    ? prevSeriesValues.reduce((a, b) => a + b, 0) / prevSeriesValues.length
    : null

  // % хороших дней сна (текущий/предыдущий период) — больше не используется в UI,
  // удалено вместе с badge'м. Если понадобится снова — пересчёт через sleepSeries.

  // «Вес с поправкой» (EMA) считается по той же полной 365-дневной истории и
  // тем же helper'ом из @tfa/shared, что и на главной и в подробной аналитике —
  // иначе число расходится между экранами (bug #137). Тот же queryKey
  // переиспользует кэш запроса аналитики/главной.
  const weightHistEnd = new Date().toISOString().slice(0, 10)
  const weightHistStart = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10)
  const { data: weightHistory } = useQuery({
    queryKey: ['analytics', 'weight', weightHistStart, weightHistEnd],
    queryFn: () => api.getDiaryEntries({ startDate: weightHistStart, endDate: weightHistEnd }).catch(() => ({ entries: [] })),
    staleTime: 60_000,
    enabled: tab === 'weight',
  })
  const weightHistEntries = useMemo(
    () => mergeWeightSources(
      ((weightHistory?.entries ?? []) as Array<{ date: string; weight: number | null }>),
      ((weightHistMeasurements?.measurements ?? []) as Array<{ date: string; weight: number | null }>),
    ),
    [weightHistory, weightHistMeasurements],
  )

  // Для таба «Вес»: EMA по полной истории (α=0.2) + значение в видимом окне.
  const weightSmoothSeries = useMemo(() => {
    if (tab !== 'weight') return null
    return weightEmaSeries(weightHistEntries).map((p) => ({
      ts: new Date(`${p.date}T00:00:00`).getTime(),
      smooth: p.smooth,
    }))
  }, [tab, weightHistEntries])

  // Сглаженная серия в видимом окне — параллельно series по дням
  const weightSmoothPoints: Array<{ value: number | null; date: Date }> = useMemo(() => {
    if (tab !== 'weight' || !weightSmoothSeries) return []
    const byDay = new Map<string, number>()
    for (const p of weightSmoothSeries) byDay.set(toDateKey(new Date(p.ts)), p.smooth)
    // Заполняем пробелы последним известным сглаженным значением (carry-forward).
    let last: number | null = null
    return series.map((s) => {
      const k = toDateKey(s.date)
      const v = byDay.get(k)
      if (v != null) last = v
      return { value: last, date: s.date }
    })
  }, [tab, weightSmoothSeries, series])

  // Подпись «С поправкой» = самое свежее сглаженное значение по полной истории
  // (не зависит от выбранного периода) — так оно совпадает с главной и подробной
  // аналитикой (bug #137). Видимая пунктирная линия (weightSmoothPoints) при этом
  // остаётся в рамках выбранного окна.
  const weightSmoothLast = tab === 'weight' ? latestWeightEma(weightHistEntries) : null

  const [weightSmoothInfoOpen, setWeightSmoothInfoOpen] = useState(false)

  const W = 320, H = 90, PX = 8, PY = 10
  const xAt = (i: number, total: number) => total <= 1 ? PX : PX + (i / (total - 1)) * (W - 2 * PX)

  // Chart paths for active tab (замеры график не строят — у них отдельный список)
  // Для веса в Y-домен включаем и пунктирную линию «с поправкой» (EMA), иначе её
  // значения выходят за пределы графика и линия уезжает за видимую область.
  const smoothDomainValues =
    tab === 'weight'
      ? weightSmoothPoints.map((p) => p.value).filter((v): v is number => v != null)
      : []
  const allValues = [...validValues, ...smoothDomainValues]

  const min = allValues.length ? Math.min(...allValues) : 0
  const max = allValues.length ? Math.max(...allValues) : 1
  const range = max - min || 1
  const yAt = (v: number) => H - PY - ((v - min) / range) * (H - 2 * PY)

  const buildPath = (pts: { x: number; y: number | null }[]): string => {
    const valid = pts.filter((p): p is { x: number; y: number } => p.y != null)
    if (valid.length === 0) return ''
    if (valid.length === 1) return `M${valid[0].x},${valid[0].y}`
    let d = `M${valid[0].x},${valid[0].y}`
    for (let i = 0; i < valid.length - 1; i++) {
      const p0 = valid[i - 1] || valid[i]
      const p1 = valid[i]
      const p2 = valid[i + 1]
      const p3 = valid[i + 2] || p2
      const cp1x = p1.x + (p2.x - p0.x) / 6
      const cp1y = p1.y + (p2.y - p0.y) / 6
      const cp2x = p2.x - (p3.x - p1.x) / 6
      const cp2y = p2.y - (p3.y - p1.y) / 6
      d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`
    }
    return d
  }

  const singlePoints = tab === 'measurements' ? null : series.map((s, i) => ({
    x: xAt(i, series.length),
    y: s.value != null ? yAt(s.value) : null,
  }))
  const singlePath = singlePoints ? buildPath(singlePoints) : ''

  // Smooth line для веса (рисуется поверх точек)
  const weightSmoothPath = useMemo(() => {
    if (tab !== 'weight' || weightSmoothPoints.length === 0) return ''
    const pts = weightSmoothPoints.map((s, i) => ({
      x: xAt(i, weightSmoothPoints.length),
      y: s.value != null ? yAt(s.value) : null,
    }))
    return buildPath(pts)
  }, [tab, weightSmoothPoints])

  // Список изменений по всем замерам — дельта с предыдущего замера (для каждой
  // части берём два последних значения, где они есть). hasData=false → «Пока
  // замеров не было». Не зависит от выбранного периода — это «с последних замеров».
  const measurementSummary = useMemo(() => {
    const empty = { hasData: false, items: [] as { key: string; label: string; cur: number; delta: number | null; upGood: boolean }[] }
    if (!measurementByDate) return empty
    const dates = Object.keys(measurementByDate).sort() // YYYY-MM-DD по возрастанию
    if (dates.length === 0) return empty
    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)
    const upGoodKeys = new Set(['arms', 'calf']) // прирост — хорошо (мышцы)
    const items = MEAS_PARTS.map((p) => {
      const vals: number[] = []
      for (const d of dates) {
        const v = num((measurementByDate[d] as Record<string, unknown>)[p.key])
        if (v != null) vals.push(v)
      }
      if (vals.length === 0) return null
      const cur = vals[vals.length - 1]
      const prev = vals.length >= 2 ? vals[vals.length - 2] : null
      const delta = prev != null ? Math.round((cur - prev) * 10) / 10 : null
      return { key: p.key, label: p.label, cur, delta, upGood: upGoodKeys.has(p.key) }
    }).filter((x): x is NonNullable<typeof x> => x != null)
    return { hasData: items.length > 0, items }
  }, [measurementByDate])

  const periodPoints = days

  return (
    <GlassCard style={{ padding: 16, borderRadius: 18 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F }}>
          Аналитика
        </span>
        <button
          onClick={onOpenDetail}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 12,
            background: 'rgba(108,99,255,0.10)', color: '#6C63FF',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, fontFamily: F,
          }}
        >
          <BarChart3 size={14} color="#6C63FF" />
          Подробнее
        </button>
      </div>

      {/* Period selector — для замеров не показываем (список идёт с последних замеров) */}
      {tab !== 'measurements' && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, padding: 3, borderRadius: 10, background: 'rgba(26,58,92,0.06)' }}>
          {availablePeriods.map((p) => {
            const active = period === p
            return (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  flex: 1, height: 28, borderRadius: 8, border: 'none',
                  background: active ? 'white' : 'transparent',
                  color: active ? C.text : C.tertiary,
                  fontSize: 11, fontWeight: 700, fontFamily: F,
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {PERIOD_LABEL[p]}
              </button>
            )
          })}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {(['sleep', 'steps', 'weight', 'measurements'] as const).map((key) => {
          const c = tabConfig[key]
          const active = tab === key
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                flex: 1, height: 34, borderRadius: 10, border: 'none', padding: 0,
                background: active
                  ? `linear-gradient(135deg, ${c.color} 0%, ${c.color}CC 100%)`
                  : 'rgba(160,174,192,0.10)',
                color: active ? 'white' : C.secondary,
                fontSize: 11, fontWeight: 600, fontFamily: F,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                transition: 'background 0.15s',
              }}
            >
              <c.Icon size={13} color={active ? 'white' : C.secondary} />
              {c.label}
            </button>
          )
        })}
      </div>

      {/* Stat (только для steps/weight — sleep идёт по качеству ниже) */}
      {tab !== 'measurements' && tab !== 'sleep' && (
        <>
          <div style={{ marginBottom: 6, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 26, fontWeight: 700, color: cfg.color, fontFamily: F, lineHeight: 1 }}>
              {avg != null ? `${cfg.formatter(avg)}${cfg.unit ? ' ' + cfg.unit : ''}` : '—'}
            </span>
            <span style={{ fontSize: 12, color: C.secondary, fontFamily: F }}>
              среднее · {PERIOD_LABEL[period]}
            </span>
            <MiniPctBadge curr={avg} prev={prevAvg} periodLabel={PERIOD_LABEL[period]} goodWhen={tab === 'weight' ? 'down' : 'up'} />
          </div>
          {tab === 'weight' && weightSmoothLast != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ display: 'inline-block', width: 14, height: 2, background: '#00B894', borderRadius: 2, position: 'relative' }}>
                <span style={{ position: 'absolute', top: -1, left: 4, width: 6, height: 4, background: '#F5F6FA' }} />
              </span>
              <span style={{ fontSize: 12, color: '#1C2D41', fontFamily: F, fontWeight: 700 }}>
                С поправкой: {weightSmoothLast.toFixed(1)} кг
              </span>
              <button
                data-testid="mini-weight-smooth-info"
                onClick={() => setWeightSmoothInfoOpen((v) => !v)}
                aria-label="Что это"
                style={{
                  width: 16, height: 16, borderRadius: '50%', border: 'none',
                  background: 'rgba(0,184,148,0.18)', color: '#00805F',
                  fontSize: 10, fontWeight: 800, cursor: 'pointer', fontFamily: F,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1, padding: 0,
                }}
              >?</button>
            </div>
          )}
          {tab === 'weight' && weightSmoothInfoOpen && (
            <div style={{
              background: 'rgba(0,184,148,0.06)',
              border: '1px solid rgba(0,184,148,0.20)',
              borderRadius: 10, padding: '8px 10px',
              marginBottom: 6, fontFamily: F,
              position: 'relative',
            }}>
              <button
                onClick={() => setWeightSmoothInfoOpen(false)}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 6,
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 13, color: '#6B7B8D',
                  padding: 2, lineHeight: 1,
                }}
              >×</button>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#00805F', marginBottom: 3 }}>
                Вес с поправкой на колебания
              </div>
              <p style={{ fontSize: 11, color: '#3D4A5C', margin: 0, lineHeight: 1.4 }}>
                Статистический метод убирает скачки из-за воды/еды/соли. Формула верит на 20 % сегодняшнему весу и на 80 % накопленной истории.
              </p>
            </div>
          )}
        </>
      )}
      {/* Sleep — качество; Measurements — компактные дельты; иначе — график */}
      {tab === 'sleep' ? (
        sleepCounts.total > 0 ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
              {([
                { q: 'GOOD' as const,  label: 'Выспался' },
                { q: 'SO_SO' as const, label: 'Недоспал' },
                { q: 'BAD' as const,   label: 'Не выспался' },
              ]).map((row) => {
                const n = row.q === 'GOOD' ? sleepCounts.good : row.q === 'SO_SO' ? sleepCounts.soso : sleepCounts.bad
                return (
                  <div key={row.q} style={{
                    padding: '8px 6px',
                    borderRadius: 12,
                    background: `${SLEEP_COLORS[row.q]}10`,
                    border: `1px solid ${SLEEP_COLORS[row.q]}30`,
                    textAlign: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: SLEEP_COLORS[row.q], flexShrink: 0 }} />
                      <span style={{ fontSize: 16, fontWeight: 800, color: SLEEP_COLORS[row.q], lineHeight: 1 }}>{n}</span>
                    </div>
                    <div style={{ fontSize: 10, color: C.secondary, marginTop: 4, fontFamily: F }}>{row.label}</div>
                  </div>
                )
              })}
            </div>
            {/* Цветовая лента по дням периода — авто-перенос при длинных периодах
                (до 14 квадратов в ряд, дальше — следующий ряд). */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(sleepSeries.length, 14)}, 1fr)`,
              gap: 3,
              padding: '4px 2px',
            }}>
              {sleepSeries.map((s, i) => (
                <div
                  key={i}
                  title={`${s.date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}${s.q ? ' · ' + (s.q === 'GOOD' ? 'выспался' : s.q === 'SO_SO' ? 'недоспал' : 'не выспался') : ''}`}
                  style={{
                    aspectRatio: '1 / 1',
                    borderRadius: 5,
                    background: s.q != null ? SLEEP_COLORS[s.q] : 'rgba(26,58,92,0.06)',
                    boxShadow: s.q != null ? `0 0 0 1px ${SLEEP_COLORS[s.q]}33 inset` : 'none',
                  }}
                />
              ))}
            </div>
            {/* CTA «Как наладить сон?» — открывает методичку TFA */}
            <button
              data-testid="diary-sleep-guide-cta"
              onClick={() => setShowSleepGuide(true)}
              style={{
                marginTop: 10,
                width: '100%', height: 36, borderRadius: 10,
                border: '1px solid rgba(26,122,110,0.30)',
                background: 'rgba(26,122,110,0.06)',
                color: '#1A7A6E', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              💤 Как наладить сон?
            </button>
          </>
        ) : (
          <>
            <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.tertiary, fontSize: 12, fontFamily: F }}>
              Нет данных за {PERIOD_LABEL[period]}
            </div>
            <button
              data-testid="diary-sleep-guide-cta-empty"
              onClick={() => setShowSleepGuide(true)}
              style={{
                marginTop: 8,
                width: '100%', height: 36, borderRadius: 10,
                border: '1px solid rgba(26,122,110,0.30)',
                background: 'rgba(26,122,110,0.06)',
                color: '#1A7A6E', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              💤 Как наладить сон?
            </button>
          </>
        )
      ) : tab === 'measurements' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 0' }}>
          {!measurementSummary.hasData ? (
            <div data-testid="measurements-empty" style={{ padding: '14px 0', textAlign: 'center', color: C.tertiary, fontSize: 13, fontFamily: F }}>
              Пока замеров не было
            </div>
          ) : (
            measurementSummary.items.map((m) => {
              // Цвет дельты: для мышц (руки/голень) рост — хорошо, иначе — снижение.
              const good = m.delta == null || m.delta === 0 ? null : (m.upGood ? m.delta > 0 : m.delta < 0)
              return (
                <div key={m.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 15, color: C.text, fontFamily: F }}>{m.label}</span>
                  <span style={{
                    fontSize: 15, fontWeight: 700, fontFamily: F,
                    color: m.delta == null ? C.text : good === null ? C.text : good ? '#16A34A' : '#DC2626',
                  }}>
                    {m.delta == null ? `${m.cur} см` : `${m.delta > 0 ? '+' : ''}${m.delta} см`}
                  </span>
                </div>
              )
            })
          )}
          <button
            data-testid="measurements-open-analytics"
            onClick={onOpenDetail}
            style={{
              marginTop: 4, height: 40, borderRadius: 12, border: 'none',
              background: 'rgba(108,92,231,0.10)', color: '#6C5CE7',
              fontSize: 14, fontWeight: 700, fontFamily: F, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            Открыть аналитику →
          </button>
        </div>
      ) : (
        (() => {
          const hasData = !!(singlePoints && singlePoints.some(p => p.y != null))
          if (!hasData) {
            return (
              <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.tertiary, fontSize: 12, fontFamily: F }}>
                Нет данных за {PERIOD_LABEL[period]}
              </div>
            )
          }

          // Y-axis ticks (4 уровня: max, 2/3, 1/3, min)
          const yTicks: Array<{ v: number; pct: number }> = []
          if (range > 0) {
            for (let k = 0; k <= 3; k++) {
              const v = max - (range * k / 3)
              yTicks.push({ v, pct: (k / 3) * 100 })
            }
          } else {
            yTicks.push({ v: max, pct: 50 })
          }
          const fmtY = (v: number, isTop: boolean): string => {
            // Единицу добавляем только к верхней метке — чтобы не дублировать
            // на каждой и не наезжать друг на друга.
            const unit = tab === 'steps' ? '' : tab === 'weight' ? ' кг' : ' см'
            if (tab === 'steps') {
              const num = v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}к` : String(Math.round(v))
              return isTop ? `${num} шаг` : num
            }
            const num = tab === 'weight' ? v.toFixed(1) : Math.round(v).toString()
            return isTop ? `${num}${unit}` : num
          }

          // X-axis date labels — 5 равноудалённых меток
          const xLabelDates: Date[] = []
          const xLabelPcts: number[] = []
          const totalDays = series.length || days
          const labelCount = totalDays >= 30 ? 5 : totalDays >= 14 ? 4 : Math.min(totalDays, 4)
          for (let k = 0; k < labelCount; k++) {
            const idx = labelCount === 1 ? 0 : Math.round((totalDays - 1) * (k / (labelCount - 1)))
            const d = new Date(TODAY)
            d.setDate(d.getDate() - (totalDays - 1 - idx))
            xLabelDates.push(d)
            xLabelPcts.push(labelCount === 1 ? 50 : (k / (labelCount - 1)) * 100)
          }
          const fmtX = (d: Date): string => {
            // 1м/3м/6м — 'd MMM', короткие — просто 'd'
            if (totalDays >= 14) return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
            return d.toLocaleDateString('ru-RU', { day: 'numeric' })
          }

          return (
            <div style={{ position: 'relative', paddingLeft: 44, paddingBottom: 18, paddingTop: 4 }}>
              {/* Y-axis labels — единица только у верхней метки */}
              <div style={{ position: 'absolute', left: 0, top: 4, height: H, width: 40, fontFamily: F }}>
                {yTicks.map((t, i) => (
                  <span
                    key={i}
                    style={{
                      position: 'absolute',
                      top: `${t.pct}%`,
                      right: 4,
                      transform: i === 0 ? 'translateY(0)' : i === yTicks.length - 1 ? 'translateY(-100%)' : 'translateY(-50%)',
                      fontSize: 9, color: '#9CA3AF', fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtY(t.v, i === 0)}
                  </span>
                ))}
              </div>
              {/* Y-axis grid lines */}
              <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', height: H, position: 'relative', zIndex: 1 }}>
                {yTicks.map((t, i) => {
                  const y = (t.pct / 100) * H
                  return (
                    <line key={i} x1={0} x2={W} y1={y} y2={y} stroke="rgba(26,58,92,0.06)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                  )
                })}
                {singlePoints && (
                  <>
                    <path d={singlePath} fill="none" stroke={cfg.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    {periodPoints <= 30 && singlePoints.map((p, i) => p.y != null ? (
                      <circle key={i} cx={p.x} cy={p.y} r={2.6} fill={cfg.color} />
                    ) : null)}
                    {tab === 'weight' && weightSmoothPath && (
                      <path d={weightSmoothPath} fill="none" stroke="#00B894" strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    )}
                  </>
                )}
              </svg>
              {/* X-axis date labels */}
              <div style={{ position: 'absolute', left: 44, right: 0, bottom: 0, height: 14 }}>
                {xLabelDates.map((d, i) => (
                  <span
                    key={i}
                    style={{
                      position: 'absolute',
                      left: `${xLabelPcts[i]}%`,
                      transform:
                        i === 0 ? 'translateX(0)'
                        : i === xLabelDates.length - 1 ? 'translateX(-100%)'
                        : 'translateX(-50%)',
                      fontSize: 9, color: '#9CA3AF', fontFamily: F, fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtX(d)}
                  </span>
                ))}
              </div>
            </div>
          )
        })()
      )}


      {/* Period range labels убраны — теперь шкала X встроена в график */}

      <SleepGuideSheet open={showSleepGuide} onClose={() => setShowSleepGuide(false)} />
    </GlassCard>
  )
}

// ---- Main Page ----

export function Diary() {
  const { user: authUser } = useAuth()
  const isClub = authUser?.product === 'CLUB'
  // #534: в Клубе куратора нет — его комментарии и инбокс прячем целиком.
  const curatorHidden = useCuratorHidden()
  // #537: там же, где куратора нет, норму по питанию задаёт сам клиент.
  const [goalSheetOpen, setGoalSheetOpen] = useState(false)
  const { data: myGoals } = useQuery({
    queryKey: ['diary', 'nutrition-goals'],
    queryFn: () => api.getMyNutritionGoals(),
  })
  const goalSelfManaged = !!myGoals?.selfManaged
  // Demo-режим: AWAITING_QUESTIONNAIRE/AWAITING_CURATOR/AWAITING_START.
  // Сдавать отчёты и копить геймификацию ещё нельзя — UI блокирует тапы и
  // показывает toast «Доступно после старта программы». Backend всё равно
  // защищён `requireActiveState`, это лишь UX-зеркало.
  const demoStatus = (authUser as any)?.status as string | undefined
  const isDemoFlow = demoStatus === 'AWAITING_QUESTIONNAIRE'
    || demoStatus === 'AWAITING_CURATOR'
    || demoStatus === 'AWAITING_START'
  const { viewingClientId, isAdminView } = useCurator()
  const isCuratorViewing = !!viewingClientId
  const guardDemo = (next: () => void): (() => void) => () => {
    // Куратор в режиме просмотра аккаунта клиента — только чтение: не даём
    // сдавать отчёты/тренировки и т.п. за клиента (бэкенд тоже блокирует 403).
    if (isCuratorViewing) {
      toast('Режим просмотра', { description: 'Вы смотрите аккаунт клиента — изменения недоступны.' })
      return
    }
    if (isDemoFlow) {
      toast('Доступно после старта программы. Куратор согласует план.', {
        description: demoStatus === 'AWAITING_QUESTIONNAIRE'
          ? 'Сначала заполни анкету.'
          : demoStatus === 'AWAITING_CURATOR'
            ? 'Подбираем куратора — это займёт немного времени.'
            : 'Ждём, когда куратор подтвердит старт программы.',
      })
      return
    }
    next()
  }
  const { selectionHaptic } = useTelegram()
  const navigate = useNavigate()
  // openLink/notificationHaptic нужны для FatSecret connect flow и DayNutritionCard.
  const { openLink: tgOpenLink, notificationHaptic: tgHaptic } = useTelegram()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
  const [showMetricsSheet, setShowMetricsSheet] = useState(false)
  const [metricsFocus, setMetricsFocus] = useState<'sleep' | 'steps' | 'weight' | undefined>()
  const [showMeasurementsSheet, setShowMeasurementsSheet] = useState(false)
  const [showMeasurementsCongrats, setShowMeasurementsCongrats] = useState(false)
  // #526: XP за последнюю сдачу замеров — приходит из ответа API, поздравление
  // показывает его вместо захардкоженного числа.
  const [measurementsXpAwarded, setMeasurementsXpAwarded] = useState<number | null>(null)
  const [metricsCongrats, setMetricsCongrats] = useState<{ open: boolean; xp: number; note?: string }>({ open: false, xp: 0 })
  // Поп-ап «ударный режим засчитан» — показывается ПЕРВЫМ, когда отчёт закрыл
  // новый день стрика; после его закрытия проигрывается обычное поздравление.
  const [streakCelebration, setStreakCelebration] = useState<{ open: boolean; days: number; bonusTucoin: number }>({ open: false, days: 0, bonusTucoin: 0 })
  const pendingCongratsRef = useRef<null | (() => void)>(null)
  type StreakResult = { advanced?: boolean; streakDays?: number; bonusTucoin?: number } | null | undefined
  const celebrateStreakThen = useCallback((streak: StreakResult, showCongrats?: () => void) => {
    if (streak?.advanced) {
      // Новый день серии закрыт — огоньки в окне стрика должны включать сегодня.
      queryClient.invalidateQueries({ queryKey: ['streak'] })
    }
    if (streak?.advanced && (streak.streakDays ?? 0) > 0) {
      pendingCongratsRef.current = showCongrats ?? null
      setStreakCelebration({ open: true, days: streak.streakDays!, bonusTucoin: streak.bonusTucoin ?? 0 })
    } else {
      showCongrats?.()
    }
  }, [queryClient])
  const closeStreakCelebration = useCallback(() => {
    setStreakCelebration((s) => ({ ...s, open: false }))
    const cb = pendingCongratsRef.current
    pendingCongratsRef.current = null
    if (cb) setTimeout(cb, 200) // дать модалке закрыться, затем обычное поздравление
  }, [])
  const [showPhotoReportSheet, setShowPhotoReportSheet] = useState(false)
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false)
  const [showNpsSheet, setShowNpsSheet] = useState(false)
  const [showSosSheet, setShowSosSheet] = useState(false)
  // #266/#270/#286: единый инбокс комментариев куратора (табы Питание/Тренировки).
  const [inbox, setInbox] = useState<{ open: boolean; tab: 'NUTRITION' | 'WORKOUT' }>({ open: false, tab: 'NUTRITION' })
  // Куратор, открывший клиента, отвечает из инбокса своим токеном (useCommentsInbox).
  const commentsInbox = useCommentsInbox({ invalidateOnReply: [['diary']] })
  const allComments = commentsInbox.comments
  const unreadCommentsTotal = useMemo(() => allComments.filter((c) => c.isUnread).length, [allComments])
  const openInbox = (tab: 'NUTRITION' | 'WORKOUT') => {
    setInbox({ open: true, tab })
    commentsInbox.markRead()
  }
  const [showAddMealSheet, setShowAddMealSheet] = useState(false)
  const [addMealType, setAddMealType] = useState('BREAKFAST')
  const [showDayNutritionSheet, setShowDayNutritionSheet] = useState(false)
  // #306: клиент может САМ начать комментарий по питанию за день (тред
  // раскрывается по кнопке; с существующими комментариями — открыт сразу).
  const [nutritionThreadOpen, setNutritionThreadOpen] = useState(false)
  const [fsSheet, setFsSheet] = useState<{ open: boolean; mode: 'connect' | 'reconnect' }>({ open: false, mode: 'connect' })
  const [metricsValues, setMetricsValues] = useState<MetricsValues>({ sleepQuality: '', steps: '', weight: '' })
  const [trainingHighlight, setTrainingHighlight] = useState(false)
  const trainingCardRef = useRef<HTMLDivElement>(null)

  const selectedDateKey = useMemo(() => toDateKey(selectedDate), [selectedDate])
  const selectedIsFuture = useMemo(() => isFuture(selectedDate), [selectedDate])

  // #266/#270: пометка прочтения теперь через единый инбокс (openInbox →
  // markAllCommentsRead). Старый per-day markDayCommentsRead удалён.

  // Data fetching
  const { data: diaryData, isLoading: diaryLoading } = useQuery({
    queryKey: ['diary', selectedDateKey],
    queryFn: () => api.getDiary(selectedDateKey),
  })

  // FatSecret status — если не подключён, показываем DayNutritionCard вместо
  // покороткой карусели приёмов пищи (как в прототипе 314e3c6c).
  const { data: fatSecretStatus } = useQuery({
    queryKey: ['fatsecret-status'],
    queryFn: () => api.getFatSecretStatus(),
    refetchInterval: (q) => fatSecretStatusRefetchInterval(q.state.data?.connected),
    refetchOnWindowFocus: true,
  })
  const fatSecretConnected = !!fatSecretStatus?.connected
  // Три тарифа: состояние синхронизации по матрице тарифа (#546); замеры, фото,
  // показатели дня и геймификация — по ней же (этап 3).
  const featureAccess = useFeatureAccess()
  const fatSecretState = featureAccess.state('fatsecret')
  // #568: SOS — это чат с ИИ Павлом. На тарифе без ИИ (Программа: замок на
  // вкладке) кнопка вела в тот же закрытый чат — прячем вместе с вкладкой.
  const aiOpen = featureAccess.has('ai')
  const showMeasCard = featureAccess.has('measurements')
  const showPhotoCard = featureAccess.has('photos')

  // Возврат из OAuth-флоу FatSecret: статус опрашивается каждые 3с (+ refetch на
  // фокусе окна), и как только сервер подтвердил подключение — сами закрываем
  // лист «Подключить», иначе он висит поверх с кнопкой «Подключить», хотя уже
  // подключено, и его приходится закрывать вручную (bug #187).
  useEffect(() => {
    if (fatSecretConnected && fsSheet.open) {
      setFsSheet((s) => ({ ...s, open: false }))
      tgHaptic('success')
      toast('FatSecret подключён — данные синхронизируются')
    }
  }, [fatSecretConnected, fsSheet.open])

  // Включённые метрики питания (клетчатка + доп.). Управляются клиентом и куратором.
  const { data: nutritionMetricsData } = useQuery({
    queryKey: ['nutrition-metrics'],
    queryFn: () => api.getNutritionMetrics().catch(() => ({ enabled: ['fiber'] })),
  })
  const enabledMetrics = nutritionMetricsData?.enabled ?? ['fiber']

  // #400: индивидуальная цель по шагам от куратора — маска и подсказка в поле
  // ввода шагов. Нет цели (или запрос не удался) — поле живёт как раньше.
  const { data: stepsGoalData } = useQuery({
    queryKey: ['steps-goal'],
    queryFn: () => api.getStepsGoal().catch(() => ({ stepsGoal: null, defaultGoal: 10000 })),
  })
  const connectFatSecretMutation = useMutation({
    mutationFn: () => api.connectFatSecret(),
    onSuccess: (data: { authUrl: string }) => tgOpenLink(data.authUrl, { external: true }),
    onError: () => tgHaptic('error'),
  })
  // Ручной синк FatSecret — пользователь только что заполнил еду в FS и хочет
  // подтянуть её сейчас, не дожидаясь почасового планировщика.
  const syncFatSecretMutation = useMutation({
    // #402: синкаем ИМЕННО открытый день. Клиент правит еду задним числом, а
    // окно фона (сегодня) и ночной сверки (±2 дня) день постарше уже не
    // покрывают — раньше он навсегда оставался расходиться с FatSecret.
    mutationFn: () => api.syncFatSecret(selectedDateKey),
    onSuccess: (res: { mealsReceived?: number } | undefined) => {
      queryClient.invalidateQueries({ queryKey: ['fatsecret-status'] })
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // #464: FatSecret ответил, но записей за этот день у него нет. Раньше это
      // выглядело как успех — тост «Дневник обновлён», а на экране те же нули, и
      // человек делал вывод, что сломано приложение. Говорим правду и подсказываем,
      // что делать: чаще всего еда ещё не уехала из приложения FatSecret на их
      // сервер, откуда мы её и читаем.
      if ((res?.mealsReceived ?? 0) === 0) {
        tgHaptic('warning')
        toast(`FatSecret не отдал записи за ${selectedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`, {
          description: 'Проверь, что еда сохранена в приложении FatSecret. Если добавил только что — она доезжает до их сервера не сразу, попробуй через несколько минут.',
        })
        return
      }
      tgHaptic('success')
      toast('Дневник обновлён из FatSecret')
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; code?: string; message?: string } } } | null)?.response?.data
      const code = data?.error
      // 401 FATSECRET_TOKEN_EXPIRED → соединение удалено на сервере, нужен реконнект.
      if (code === 'FATSECRET_TOKEN_EXPIRED') {
        tgHaptic('warning')
        queryClient.invalidateQueries({ queryKey: ['fatsecret-status'] })
        setFsSheet({ open: true, mode: 'reconnect' })
        return
      }
      // Не-auth ошибка FatSecret (IP-вайтлист/подпись/HTTP) — показываем код, чтобы
      // причина была видна, а не «молча не обновилось».
      if (code === 'FATSECRET_API_ERROR') {
        tgHaptic('error')
        queryClient.invalidateQueries({ queryKey: ['fatsecret-status'] })
        toast(`FatSecret недоступен (код ${data?.code ?? '?'}) — данные не подтянулись`, {
          description: data?.code === '21' ? 'IP сервера не в вайтлисте FatSecret.' : data?.message,
        })
        return
      }
      tgHaptic('error')
      toast('Не удалось обновить дневник FatSecret')
    },
  })
  const diary = diaryData?.entry ?? null
  const nutritionGoal = diaryData?.nutritionGoal ?? null

  const saveMetricsMutation = useMutation({
    mutationFn: async (values: MetricsValues) => {
      const sleepQuality = values.sleepQuality === '' ? undefined : values.sleepQuality
      const steps = values.steps.trim() === '' ? undefined : Number(values.steps.replace(/\s/g, ''))
      const weight = values.weight.trim() === '' ? undefined : Number(values.weight.replace(',', '.'))
      return api.createDiaryEntry({
        date: selectedDateKey,
        ...(sleepQuality ? { sleepQuality } : {}),
        ...(Number.isFinite(steps as number) ? { steps } : {}),
        ...(Number.isFinite(weight as number) ? { weight } : {}),
      })
    },
    // #635: на мобильной сети связь пропадает на десятки секунд (на проде — iPhone
    // на LTE: минуту не доходило ничего, а каждое «Сохранить» сразу давало ошибку,
    // и введённое терялось). Сетевой сбой переживаем повторами с паузой; ответ
    // сервера (4xx/5xx) — нет, он сам не пройдёт.
    retry: (failures, err) => failures < 3 && describeApiError(err).isNetwork,
    retryDelay: (attempt) => 2000 * 2 ** attempt,
    onMutate: async (values: MetricsValues) => {
      // Optimistic update so MetricsRow widgets and the day-strip dot reflect
      // the new values immediately, without waiting for the server round-trip.
      await queryClient.cancelQueries({ queryKey: ['diary', selectedDateKey] })
      type DiaryQueryShape = { entry: DiaryEntryData | null; nutritionGoal: any | null }
      const previous = queryClient.getQueryData<DiaryQueryShape | null | undefined>(['diary', selectedDateKey])
      const prevEntry = previous?.entry ?? null
      const sleepQuality = values.sleepQuality === '' ? undefined : values.sleepQuality
      const steps = values.steps.trim() === '' ? undefined : Number(values.steps.replace(/\s/g, ''))
      const weight = values.weight.trim() === '' ? undefined : Number(values.weight.replace(',', '.'))
      // Compute newly-filled fields BEFORE applying optimistic update — each = 1 XP.
      let newlyFilled = 0
      if (sleepQuality && prevEntry?.sleepQuality == null) newlyFilled++
      if (Number.isFinite(steps as number) && (prevEntry?.steps == null || prevEntry.steps === 0)) newlyFilled++
      if (Number.isFinite(weight as number) && prevEntry?.weight == null) newlyFilled++
      queryClient.setQueryData<DiaryQueryShape | null | undefined>(['diary', selectedDateKey], (old) => ({
        entry: {
          ...((old?.entry ?? { id: '', date: selectedDateKey }) as DiaryEntryData),
          ...(sleepQuality ? { sleepQuality } : {}),
          ...(Number.isFinite(steps as number) ? { steps } : {}),
          ...(Number.isFinite(weight as number) ? { weight } : {}),
        },
        nutritionGoal: old?.nutritionGoal ?? null,
      }))
      return { previous, newlyFilled }
    },
    onError: (err: unknown, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(['diary', selectedDateKey], ctx.previous)
      }
      const code = (err as { response?: { data?: { code?: string } } } | null)?.response?.data?.code
      if (code === 'READ_ONLY') {
        toast.error('Программа завершена — режим просмотра')
      } else if (code === 'DEMO_MODE') {
        toast.error('Сохранение недоступно в demo-режиме')
      } else if (describeApiError(err).isNetwork) {
        // Окно с введёнными значениями остаётся открытым — повторить можно сразу.
        toast.error('Нет связи — показатели не сохранились', {
          description: 'Проверь интернет и нажми «Сохранить» ещё раз.',
        })
      } else {
        toast.error('Не удалось сохранить показатели')
      }
    },
    onSuccess: (data: { entry?: DiaryEntryData } | undefined, _vars, context) => {
      // Replace optimistic data with the authoritative server entry.
      // Keep the existing nutritionGoal — POST /diary/entry doesn't return it.
      if (data?.entry) {
        queryClient.setQueryData(['diary', selectedDateKey], (old: any) => ({
          entry: data.entry,
          nutritionGoal: old?.nutritionGoal ?? null,
        }))
      }
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['actions', 'daily-status'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      const earnedXp = (context as { newlyFilled?: number } | undefined)?.newlyFilled ?? 0
      setShowMetricsSheet(false)
      setMetricsFocus(undefined)
      // Стрик-поп-ап первым (если закрылся новый день), затем обычное поздравление.
      // За прошлые дни XP не начисляется (bug #221): плашка показывается, но без
      // «+XP» и с пояснением. За «сегодня» — как раньше.
      const past = isPastDate(selectedDate)
      celebrateStreakThen((data as { streak?: StreakResult })?.streak, () => {
        if (earnedXp > 0) {
          setMetricsCongrats({
            open: true,
            xp: past ? 0 : earnedXp,
            note: past ? 'За прошлые дни прогресс не начисляется' : undefined,
          })
        } else {
          toast.success('Показатели обновлены')
        }
      })
    },
  })

  // NOTE: dailyStatus query intentionally removed — ReportsBlock now derives state
  // directly from `diary` (selected date entry) so past days reflect their own data.

  const { data: measurementsData, isLoading: measurementsLoading } = useQuery({
    queryKey: ['diary', 'measurements'],
    queryFn: () => api.getMeasurements(),
  })
  type MeasurementRecord = {
    date: string
    weight?: number | null
    arms?: number | null
    chest?: number | null
    waist?: number | null
    belly?: number | null
    pelvis?: number | null
    hips?: number | null
    thigh?: number | null
    calf?: number | null
    neck?: number | null
  }
  const measurementByDate = useMemo<Record<string, MeasurementRecord>>(() => {
    const map: Record<string, MeasurementRecord> = {}
    // Server returns measurements ordered by date desc — first occurrence per day = latest
    const list = (measurementsData?.measurements ?? []) as MeasurementRecord[]
    for (const m of list) {
      const key = labelDateKey(m.date)
      if (!map[key]) map[key] = m
    }
    return map
  }, [measurementsData])
  // A day counts as "Замеры сделаны" only when at least one circumference is set.
  // Pure weight-only records (legacy, before weight moved to MetricsRow) and empty
  // rows must NOT light up the measurements dot/checkmark.
  const measurementsByDate = useMemo<Set<string>>(() => {
    const fields = ['arms', 'chest', 'waist', 'belly', 'pelvis', 'thigh', 'calf', 'neck'] as const
    const set = new Set<string>()
    for (const [key, m] of Object.entries(measurementByDate)) {
      if (fields.some((f) => typeof m[f] === 'number' && (m[f] as number) > 0)) {
        set.add(key)
      }
    }
    return set
  }, [measurementByDate])
  // Дата записи замеров в том же цикле (Сб→Пт), что и выбранный день, или null.
  // Используется MeasurementsSheet для locked-вью: один Measurement на цикл, на
  // других днях цикла сдать повторно нельзя — модалка показывает «уже сданы DD.MM».
  // #509: цикл считает measurementCycleStart из @tfa/shared — той же функцией,
  // что и карточка (measCycle). Пока шторка жила по ISO-неделе, а карточка по
  // окну, они расходились (#3); теперь у правила одна реализация.
  const weeklyMeasurementKey = useMemo<string | null>(() => {
    const cycleStart = measurementCycleStart(selectedDate)
    const cycleEnd = nextMeasurementCycleStart(selectedDate)
    for (const key of measurementsByDate) {
      const d = new Date(`${key}T00:00:00`)
      if (d >= cycleStart && d < cycleEnd) return key
    }
    return null
  }, [measurementsByDate, selectedDate])

  const lastMeasurementValues = useMemo(() => {
    const fields = ['weight', 'arms', 'chest', 'waist', 'belly', 'pelvis', 'hips', 'thigh', 'calf', 'neck'] as const
    const result: Partial<Record<typeof fields[number], number>> = {}
    const list = (measurementsData?.measurements ?? []) as MeasurementRecord[]
    // list is already date-desc; first non-null per field wins
    for (const m of list) {
      for (const key of fields) {
        if (result[key] == null && m[key] != null) {
          result[key] = m[key] as number
        }
      }
    }
    return result
  }, [measurementsData])

  const { data: workoutEntriesData } = useQuery({
    queryKey: ['workout', 'entries-range'],
    queryFn: () => api.getWorkoutEntrySummaries(),
  })
  type WorkoutEntrySummary = {
    id: string
    date: string
    programDayId?: string | null
    durationMinutes?: number | null
    exercises?: Array<{ id: string; sets?: Array<unknown> }>
    cardioActivities?: Array<unknown>
  }
  const workoutByDate = useMemo<Record<string, WorkoutEntrySummary>>(() => {
    const map: Record<string, WorkoutEntrySummary> = {}
    const list = (workoutEntriesData?.entries ?? []) as WorkoutEntrySummary[]
    for (const w of list) {
      const key = labelDateKey(w.date)
      if (!map[key]) map[key] = w
    }
    return map
  }, [workoutEntriesData])
  const workoutsByDate = useMemo<Set<string>>(
    () => new Set(Object.keys(workoutByDate)),
    [workoutByDate],
  )
  // Силовая vs кардио по датам — для блока «Отчёты»: силовая тренировка (+10 XP)
  // и отдельная кардио-активность (+5 XP) считаются раздельно, чтобы кардио не
  // съедало силовую квоту и давало собственный пункт в зачёте дня/недели.
  const strengthByDate = useMemo<Set<string>>(() => {
    const s = new Set<string>()
    for (const w of (workoutEntriesData?.entries ?? []) as WorkoutEntrySummary[]) {
      if ((w.exercises?.length ?? 0) > 0) s.add(labelDateKey(w.date))
    }
    return s
  }, [workoutEntriesData])
  const cardioByDate = useMemo<Set<string>>(() => {
    const s = new Set<string>()
    for (const w of (workoutEntriesData?.entries ?? []) as WorkoutEntrySummary[]) {
      // +5 XP начисляется только за активность ≥ 20 мин (XP_REWARDS.TRAINING_CARDIO.minMinutes),
      // поэтому короткие кардио в зачёт «Отчётов» не попадают.
      if ((w.cardioActivities?.length ?? 0) > 0 && (w.durationMinutes ?? 0) >= 20) s.add(labelDateKey(w.date))
    }
    return s
  }, [workoutEntriesData])
  // #205: даты просмотра лекций → пункт «Лекция» в шкале «Отчёты» (день/неделя),
  // появляется только по факту просмотра (как кардио). Count — для недельного зачёта.
  const { data: lectureCompletionsData } = useQuery({
    queryKey: ['lectures', 'completions'],
    queryFn: () => api.getLectureCompletions().catch(() => ({ completedAt: [] as string[] })),
  })
  const lectureCountByDate = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>()
    for (const ts of (lectureCompletionsData?.completedAt ?? [])) {
      const k = toDateKey(new Date(ts))
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return m
  }, [lectureCompletionsData])
  // Aggregates for the training-card stats row (count / avg duration / current streak).
  // Computed from the same range we already fetch — no extra request needed.
  // Workout-module aggregates — count + average duration. Streak is global,
  // not per-module (see docs/19), so it's fetched separately from the dashboard.
  const trainingAggregates = useMemo(() => {
    const list = (workoutEntriesData?.entries ?? []) as Array<{ date: string; durationMinutes?: number | null }>
    const count = list.length
    const withDuration = list.filter((w) => (w.durationMinutes ?? 0) > 0)
    const avgMin = withDuration.length > 0
      ? Math.round(withDuration.reduce((sum, w) => sum + (w.durationMinutes as number), 0) / withDuration.length)
      : 0
    return { count, avgMin }
  }, [workoutEntriesData])

  // Global daily-action streak (per docs/19): pulled from the same dashboard
  // payload Home uses. Cache key matches Home so both pages share it.
  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
  })
  const streakDays = (dashboardData as { streak?: number } | undefined)?.streak ?? 0

  // Active-workout draft — written by WorkoutActive on fresh start, cleared on save.
  // We only need to read it once at mount; navigation away from /diary remounts this page.
  // `startedAt` is the absolute timestamp of when the workout began — used by
  // TrainingCard to render the running timer based on real elapsed time, not a
  // counter that resets when the diary tab remounts.
  const workoutDraft = useMemo<{ date: string; startedAt: number; programDayId: string | null } | null>(() => {
    try {
      const raw = localStorage.getItem('workout-draft')
      if (!raw) return null
      const parsed = JSON.parse(raw) as { date?: string; ts?: number; startedAt?: number; programDay?: { id?: string } | null } | null
      if (!parsed?.date || !parsed?.ts) return null
      // Auto-expire matches the WorkoutActive draft TTL (24h).
      if (Date.now() - parsed.ts > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('workout-draft')
        return null
      }
      return { date: parsed.date, startedAt: parsed.startedAt ?? parsed.ts, programDayId: parsed.programDay?.id ?? null }
    } catch {
      return null
    }
  }, [])

  // #633: черновик живёт в localStorage телефона, а Telegram при выгрузке
  // мини-апа его не всегда сохраняет — после холодного старта карточка
  // показывала «Начать», и клиентка начала тренировку заново поверх идущей.
  // Без черновика берём незакрытую сессию с сервера.
  const { data: activeWorkoutSession } = useQuery({
    queryKey: ['workout', 'active-session', 'diary'],
    queryFn: () => api.getActiveWorkoutSession().catch(() => ({ session: null })),
    enabled: !workoutDraft,
    staleTime: 30_000,
  })
  const effectiveWorkoutDraft = useMemo(() => {
    if (workoutDraft) return workoutDraft
    const session = activeWorkoutSession?.session
    if (!session) return null
    return { date: localDateKey(session.workoutDate), startedAt: new Date(session.startedAt).getTime(), programDayId: session.programDayId ?? null }
  }, [workoutDraft, activeWorkoutSession])

  const { data: workoutProgramData } = useQuery({
    queryKey: ['workout', 'program'],
    queryFn: () => api.getWorkoutProgram().catch(() => null),
  })
  const hasProgram = useMemo(() => {
    const programData = (workoutProgramData as any)?.program || workoutProgramData
    return !!(programData && programData.status === 'ACTIVE')
  }, [workoutProgramData])
  // Дней в активной программе — недельный лимит тренировок в шкале отчётов.
  const programDaysCount = useMemo<number | null>(() => {
    const programData = (workoutProgramData as any)?.program || workoutProgramData
    if (!programData || programData.status !== 'ACTIVE') return null
    return programData.daysCount ?? programData.days?.length ?? null
  }, [workoutProgramData])

  const { data: unreadData } = useQuery({
    queryKey: ['diary', 'unread-count'],
    queryFn: () => api.getUnreadCommentsCount(),
    staleTime: 20_000,
  })
  const unreadByType = unreadData?.byType ?? {
    DIARY_ENTRY: 0, DIARY_MEAL: 0, MEASUREMENT: 0,
    PROGRESS_PHOTO: 0, WORKOUT_EXERCISE: 0, WORKOUT_ENTRY: 0,
  }

  const { data: unreadByDateData } = useQuery({
    queryKey: ['diary', 'unread-by-date'],
    queryFn: () => api.getUnreadCommentsByDate(),
    staleTime: 20_000,
  })
  const unreadByDate = unreadByDateData?.byDate ?? {}

  // Determine today's program day (exercises + day number for the badge).
  // «Следующий день» считает сервер: последний выполненный день программы + 1
  // (циклично). Раньше день брался по дню недели — клиент видел не тот день.
  const todayProgramDay = useMemo<ProgramDay | undefined>(() => {
    const programData = (workoutProgramData as any)?.program || workoutProgramData
    if (!programData?.days?.length) return undefined
    const days = programData.days as ProgramDay[]
    const nextDayNumber: number | undefined = (workoutProgramData as any)?.progression?.nextDayNumber
    return days.find((d) => d.dayNumber === nextDayNumber) ?? days[0]
  }, [workoutProgramData])
  const todayExercises = todayProgramDay?.exercises

  // What is planned for the selected (future) date — derived from the program.
  const plannedTypeForSelected = useMemo<'strength' | 'cardio' | 'none'>(() => {
    const programData = (workoutProgramData as any)?.program || workoutProgramData
    if (!programData?.days?.length) return 'none'
    const days = programData.days as ProgramDay[]
    const dayIndex = days.length === 1 ? 0 : (selectedDate.getDay() || 7) % days.length
    const day = days[dayIndex]
    const exercises = (day?.exercises ?? []) as Array<{ exercise?: { category?: { workoutType?: { kind?: string } } } }>
    if (!exercises.length) return 'none'
    const hasCardio = exercises.some((e) => e.exercise?.category?.workoutType?.kind === 'CARDIO')
    const hasStrength = exercises.some((e) => e.exercise?.category?.workoutType?.kind !== 'CARDIO')
    if (hasStrength) return 'strength'
    if (hasCardio) return 'cardio'
    return 'none'
  }, [workoutProgramData, selectedDate])

  // Range covering both the day-strip (TODAY -60 / +14) and the visible calendar
  // month (±1 month buffer). Strip back-window widened so dots для прошлой
  // активности подгружаются вместе с самой пролистываемой шкалой.
  const diaryRange = useMemo(() => {
    const stripStart = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - 62)
    const stripEnd = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() + 31)
    const calStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
    const calEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 2, 0)
    const start = calStart < stripStart ? calStart : stripStart
    const end = calEnd > stripEnd ? calEnd : stripEnd
    return { startDate: toDateKey(start), endDate: toDateKey(end) }
  }, [calendarMonth])

  const { data: diaryRangeData } = useQuery({
    queryKey: ['diary', 'range', diaryRange.startDate, diaryRange.endDate],
    queryFn: () => api.getDiaryEntries({ startDate: diaryRange.startDate, endDate: diaryRange.endDate }),
  })

  // Даты сдачи видео техники (#234) за тот же диапазон — для пункта «Техника» в
  // отчёте (день/неделя) на всех днях, не только сегодня. earned = принёс XP.
  const { data: techniqueDatesData } = useQuery({
    queryKey: ['diary', 'technique-dates', diaryRange.startDate, diaryRange.endDate],
    queryFn: () => api.getTechniqueDates({ startDate: diaryRange.startDate, endDate: diaryRange.endDate }).catch(() => ({ dates: [] })),
  })
  const techniqueByDate = useMemo<Set<string>>(
    () => new Set((techniqueDatesData?.dates ?? []).map((d) => d.date)),
    [techniqueDatesData],
  )
  const techniqueEarnedByDate = useMemo<Set<string>>(
    () => new Set((techniqueDatesData?.dates ?? []).filter((d) => d.earned).map((d) => d.date)),
    [techniqueDatesData],
  )

  // #507: дни, за которые отчёт РЕАЛЬНО принёс XP. Запись в дневнике и
  // начисление — разные вещи: правки задним числом и бэкфилл FatSecret пишут
  // день без XP (гард «день в день»). Шкала «Отчёты» считает заработанное по
  // этой карте, иначе она обещает баллы, которых в рейтинге нет.
  const { data: xpDaysData } = useQuery({
    queryKey: ['diary', 'xp-days', diaryRange.startDate, diaryRange.endDate],
    queryFn: () => api.getXpDays({ startDate: diaryRange.startDate, endDate: diaryRange.endDate }).catch(() => ({ days: [] })),
  })
  const xpEarnedByDate = useMemo<Map<string, Set<string>> | undefined>(
    () => (xpDaysData ? new Map(xpDaysData.days.map((d) => [d.date, new Set(d.types)])) : undefined),
    [xpDaysData],
  )

  // Build cache of diary entries by date key for dot indicators in strip + calendar
  const diaryCache = useMemo<Record<string, DiaryEntryData | undefined>>(() => {
    const cache: Record<string, DiaryEntryData | undefined> = {}
    const entries = (diaryRangeData?.entries ?? []) as DiaryEntryData[]
    for (const e of entries) {
      cache[labelDateKey(e.date)] = e
    }
    // Overlay the freshly fetched selected-day entry so optimistic updates win
    if (diary) cache[selectedDateKey] = diary
    return cache
  }, [diaryRangeData, diary, selectedDateKey])

  // #318: последний реально сданный вес (для серой маски-подсказки в поле «Вес»,
  // когда за выбранный день веса ещё нет). Раньше маска падала в захардкоженный
  // '78.5' — пользователю казалось, что подставили чужое значение. Берём самый
  // свежий не-null diary.weight СТРОГО до выбранного дня; если истории дневника
  // нет — последний вес из замеров. Ничего не подставляем в value, только hint.
  const lastKnownWeight = useMemo<number | null>(() => {
    let bestKey: string | null = null
    let bestWeight: number | null = null
    for (const [key, e] of Object.entries(diaryCache)) {
      if (!e || e.weight == null || key >= selectedDateKey) continue
      if (bestKey == null || key > bestKey) { bestKey = key; bestWeight = e.weight }
    }
    if (bestWeight != null) return bestWeight
    return lastMeasurementValues.weight ?? null
  }, [diaryCache, selectedDateKey, lastMeasurementValues])

  // Есть ли несохранённый (непрочитанный) комментарий куратора по питанию за
  // текущий день — диктует красную точку на блоке Питания.
  const hasUnreadNutritionComment = useMemo(() => {
    if (!diary) return false
    // Только питание (DIARY_ENTRY) — комментарий к показателям (DIARY_METRICS)
    // висит на той же записи дня, но относится к другому блоку.
    const dayUnread = (diary.comments ?? []).some((c: CuratorCommentDto) => c.isUnread && (c.targetType ?? 'DIARY_ENTRY') === 'DIARY_ENTRY')
    if (dayUnread) return true
    return (diary.meals ?? []).some((m: DiaryMeal) =>
      (m.comments ?? []).some((c: CuratorCommentDto) => c.isUnread)
    )
  }, [diary])

  // Комментарий куратора к блоку показателей (сон/шаги/вес) за день.
  const metricsComment = useMemo<CuratorCommentDto | null>(() => {
    if (!diary) return null
    const list = (diary.comments ?? []).filter((c: CuratorCommentDto) => (c.targetType ?? '') === 'DIARY_METRICS')
    return list.length ? list[list.length - 1] : null
  }, [diary])
  const hasUnreadMetricsComment = useMemo(
    () => (diary?.comments ?? []).some((c: CuratorCommentDto) => c.isUnread && (c.targetType ?? '') === 'DIARY_METRICS'),
    [diary]
  )


  const handleSelectDate = (d: Date) => {
    selectionHaptic()
    setSelectedDate(d)
    // Sync calendar month when selecting a date
    setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1))
    // #306: раскрытый композер комментария не должен «переезжать» на другой день.
    setNutritionThreadOpen(false)
  }

  const handleToggleCalendar = () => {
    selectionHaptic()
    setCalendarOpen((v) => !v)
  }

  const handleCalendarPrev = () => {
    selectionHaptic()
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const handleCalendarNext = () => {
    selectionHaptic()
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  const handleGoToToday = () => {
    selectionHaptic()
    setSelectedDate(new Date())
    setCalendarMonth(new Date(TODAY.getFullYear(), TODAY.getMonth(), 1))
  }

  const handleAddMeal = (type: string) => guardDemo(() => {
    selectionHaptic()
    setAddMealType(type)
    setShowAddMealSheet(true)
  })()

  const handleTrainingNavigate = () => guardDemo(() => {
    selectionHaptic()
    // "Продолжить тренировку" — go straight to the active workout view to resume the draft.
    if (trainingStatus === 'active') {
      navigate(`/diary/workout/active?date=${selectedDateKey}`)
    } else {
      // Forward the selected date so the Workouts overview pre-selects the same day.
      navigate(`/diary/workout?date=${selectedDateKey}`)
    }
  })()

  const handleOpenMeasurements = () => guardDemo(() => {
    selectionHaptic()
    setShowMeasurementsSheet(true)
  })()

  // Срочная периодическая карточка «Замеры тела» сдаёт замеры за ТЕКУЩЕЕ окно.
  // Форма пишет запись на `selectedDateKey`, поэтому если пользователь листал
  // календарь и выбрал день вне окна (Сб→Вс→Пн), замер ложился на тот день и
  // measCycle не засчитывал его → карточка зависала в «СРОЧНО». Перед открытием
  // принудительно ставим сегодня (карточка кликабельна только когда окно открыто,
  // т.е. сегодня всегда внутри окна).
  const handleOpenMeasurementsForWindow = () => guardDemo(() => {
    selectionHaptic()
    setSelectedDate(new Date())
    setShowMeasurementsSheet(true)
  })()

  // Sync metrics values from diary data.
  // #318: НЕ гейтим на `if (diary)` — в новый день записи ещё нет (diary == null),
  // и раньше эффект не срабатывал → поле веса падало в захардкоженный '78.5'.
  // Теперь всегда подставляем в серую маску последний реально сданный вес
  // (lastKnownWeight), пока за сам день веса нет. Только hint — value в
  // MetricsEditSheet стартует пустым, вводить надо самому (bug #215).
  useEffect(() => {
    setMetricsValues({
      sleepQuality: diary?.sleepQuality ?? '',
      steps: diary?.steps != null ? String(diary.steps) : '',
      weight: diary?.weight != null
        ? String(diary.weight)
        : (lastKnownWeight != null ? String(lastKnownWeight) : ''),
    })
  }, [diary, lastKnownWeight])

  // ?focus=<task> from Home tasks — open the matching sheet once, then clear the param
  useEffect(() => {
    const focus = searchParams.get('focus')
    if (!focus) return
    switch (focus) {
      case 'sleep':
      case 'steps':
      case 'weight':
        setMetricsFocus(focus)
        setShowMetricsSheet(true)
        break
      case 'food': {
        // Open the first unfilled meal, else default to breakfast
        const order = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'] as const
        const filled = new Set(((diary?.meals ?? []) as DiaryMeal[]).map((m) => m.mealType))
        const first = order.find((t) => !filled.has(t)) ?? 'BREAKFAST'
        setAddMealType(first)
        setShowAddMealSheet(true)
        break
      }
      case 'measurements':
        setShowMeasurementsSheet(true)
        break
      case 'training': {
        // Scroll to the training widget and briefly highlight it
        const el = trainingCardRef.current
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' })
            setTrainingHighlight(true)
            setTimeout(() => setTrainingHighlight(false), 1600)
          }, 50)
        }
        break
      }
    }
    // Clear the param so the sheet doesn't re-open on back/forward or refetch
    const next = new URLSearchParams(searchParams)
    next.delete('focus')
    setSearchParams(next, { replace: true })
  }, [searchParams, diary, setSearchParams])

  const meals: DiaryMeal[] = diary?.meals ?? []

  // ── Periodic cycle states for Замеры (7d) и Фото (30d closed + 7d window) ──
  const { data: photosData, isLoading: photosLoading } = useQuery({
    queryKey: ['photos'],
    queryFn: () => api.getProgressPhotos(),
  })

  // ── Допы: статус анкет обратной связи / NPS (канал «Допы») ──
  const { data: surveyStatus } = useQuery({
    queryKey: ['surveyStatus'],
    queryFn: () => api.getSurveyStatus(),
    enabled: !isClub,
  })
  // Закрытые (скрытые) пользователем заполненные анкеты. #331: источник истины —
  // сервер (ClientSettings.uiFlags.dismissedSurveys): localStorage в Telegram
  // WebView не переживает перезаход/хард-резет, а resetSessionAndReload вайпает
  // ключи tfa_* — закрытая плашка возвращалась. Локальный ключ остаётся лишь
  // мгновенным кэшем; серверный список объединяется с ним.
  const DISMISSED_SURVEYS_KEY = 'tfa_dismissed_surveys'
  const { data: uiSettingsForSurveys } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
    staleTime: 60_000,
    enabled: !isClub,
  })
  const [localDismissed, setLocalDismissed] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(DISMISSED_SURVEYS_KEY) || '[]')) } catch { return new Set() }
  })
  const dismissedSurveys = useMemo(() => {
    const server = (uiSettingsForSurveys?.uiFlags as { dismissedSurveys?: string[] } | undefined)?.dismissedSurveys ?? []
    return new Set<string>([...localDismissed, ...server])
  }, [localDismissed, uiSettingsForSurveys])
  const dismissSurvey = (slot: string) => {
    const next = new Set(dismissedSurveys); next.add(slot)
    setLocalDismissed(next)
    try { localStorage.setItem(DISMISSED_SURVEYS_KEY, JSON.stringify([...next])) } catch { /* ignore */ }
    // Персист на сервер — полный объединённый список (merge на бэке по ключу).
    api.updateSettings({ uiFlags: { dismissedSurveys: [...next] } }).catch(() => { /* ignore */ })
  }
  // Анкеты к показу: открытые/заполненные, КРОМЕ закрытых пользователем (×).
  // Закрытие важно проверять первым: заполненная анкета может всё ещё иметь
  // open=true (окно не закрылось) — тогда крестик не скрывал бы её (bug).
  const openSurveyExtras = (surveyStatus?.extras ?? []).filter(
    (e) => (e.kind === 'FEEDBACK' || e.kind === 'NPS')
      && !dismissedSurveys.has(e.slot)
      && (e.open || e.claimed)
  )
  // Мягкий гейт (#PRD): анкета ОС открыта и ещё не заполнена → авто-открываем её
  // один раз за сессию (можно отложить кнопкой «Позже»). claimed = TuCoin начислен = заполнена.
  const feedbackDue = (surveyStatus?.extras ?? []).some((e) => e.kind === 'FEEDBACK' && e.open && !e.claimed)
  const npsDue = (surveyStatus?.extras ?? []).some((e) => e.kind === 'NPS' && e.open && !e.claimed)
  useEffect(() => {
    if (!feedbackDue) return
    try {
      if (sessionStorage.getItem('tfa_feedback_prompted') === '1') return
      sessionStorage.setItem('tfa_feedback_prompted', '1')
    } catch { /* ignore */ }
    setShowFeedbackSheet(true)
  }, [feedbackDue])
  // Финальная анкета (#PRD): тот же мягкий гейт — авто-открытие раз за сессию.
  useEffect(() => {
    if (!npsDue) return
    try {
      if (sessionStorage.getItem('tfa_nps_prompted') === '1') return
      sessionStorage.setItem('tfa_nps_prompted', '1')
    } catch { /* ignore */ }
    setShowNpsSheet(true)
  }, [npsDue])
  type PeriodicState = 'open' | 'done' | 'closed' | 'overdue'
  type PeriodicInfo = {
    state: PeriodicState
    lastSubmittedAt: Date | null
    nextOpenAt: Date | null
    windowEndAt: Date | null
    /** #557: только у расписания по слотам (photoCycleFromExtras). */
    missedWindowEndAt?: Date | null
  }
  const computeCycle = (lastDate: Date | null, closedDays: number, windowDays: number): PeriodicInfo => {
    const now = new Date()
    if (!lastDate) {
      const windowEnd = new Date(now.getTime() + windowDays * 86400000)
      return { state: 'open', lastSubmittedAt: null, nextOpenAt: now, windowEndAt: windowEnd }
    }
    const nextOpen = new Date(lastDate.getTime() + closedDays * 86400000)
    const windowEnd = new Date(nextOpen.getTime() + windowDays * 86400000)
    if (now < nextOpen) return { state: 'done', lastSubmittedAt: lastDate, nextOpenAt: nextOpen, windowEndAt: windowEnd }
    if (now <= windowEnd) return { state: 'open', lastSubmittedAt: lastDate, nextOpenAt: nextOpen, windowEndAt: windowEnd }
    return { state: 'overdue', lastSubmittedAt: lastDate, nextOpenAt: nextOpen, windowEndAt: windowEnd }
  }
  const measurementsLast = useMemo<Date | null>(() => {
    // `measurementsByDate` — это Set<string> с ключами-датами (YYYY-MM-DD).
    // Берём ключи через Array.from: Object.keys(Set) ВСЕГДА возвращает [],
    // из-за чего measurementsLast оставался null, measCycle никогда не
    // переходил в 'done' и карточка «Замеры тела» вечно висела в «СРОЧНО»,
    // даже когда замеры уже сданы.
    const keys = Array.from(measurementsByDate)
    if (!keys.length) return null
    const sorted = keys.sort()
    return new Date(`${sorted[sorted.length - 1]}T00:00:00`)
  }, [measurementsByDate])
  // Дни, в которые сданы фото прогресса — для пункта «Фото» в шкалах отчётов
  // и +10 к рингу дня сдачи.
  const photosByDate = useMemo<Set<string>>(() => {
    const photos = (photosData as { photos?: Array<{ takenAt: string }> } | undefined)?.photos ?? []
    const set = new Set<string>()
    for (const p of photos) {
      const d = new Date(p.takenAt)
      if (!Number.isNaN(d.getTime())) set.add(toDateKey(d))
    }
    return set
  }, [photosData])

  const photosLast = useMemo<Date | null>(() => {
    const photos = (photosData as { photos?: Array<{ takenAt: string }> } | undefined)?.photos ?? []
    if (!photos.length) return null
    const sorted = [...photos].sort((a, b) => new Date(b.takenAt).getTime() - new Date(a.takenAt).getTime())
    return new Date(sorted[0].takenAt)
  }, [photosData])
  // #509: замеры — раз в цикл, окно сдачи Сб→Вс→Пн, дальше закрыто до субботы.
  // В #3 окно временно заменили на ISO-неделю (Пн–Вс), чтобы свести карточку со
  // шторкой; при этом клиент, вносящий замеры в понедельник, оказывался заперт
  // ровно до следующего понедельника — включая выходные, когда он и меряется.
  // Окно вернули, а расхождение #3 снято иначе: обе точки считают цикл одной и
  // той же функцией из @tfa/shared.
  const measCycle = useMemo<PeriodicInfo>(() => {
    const now = new Date()
    const cycleStart = measurementCycleStart(now)
    const windowEnd = measurementWindowEnd(now) // понедельник 23:59
    const nextOpen = nextMeasurementCycleStart(now) // следующая суббота 00:00

    const submittedInCycle = measurementsLast
      ? measurementsLast >= cycleStart && measurementsLast < nextOpen
      : false

    if (submittedInCycle) {
      return { state: 'done', lastSubmittedAt: measurementsLast, nextOpenAt: nextOpen, windowEndAt: windowEnd }
    }
    if (isMeasurementWindowOpen(now)) {
      return { state: 'open', lastSubmittedAt: measurementsLast, nextOpenAt: cycleStart, windowEndAt: windowEnd }
    }
    // Вт–Пт и не сдано: окно этого цикла упущено, ждём субботу.
    return {
      state: 'closed',
      lastSubmittedAt: measurementsLast,
      nextOpenAt: nextOpen,
      windowEndAt: measurementWindowEnd(nextOpen),
    }
  }, [measurementsLast])
  // #490: расписание фото — по слотам программы с сервера, а не по своему
  // циклу «30 дней от последнего снимка». Два расписания разъезжались, и
  // карточка показывала «Сдан» в тот момент, когда фото месяца уже требовалось.
  // Фолбэк на прежний расчёт остаётся на случай, если статус ещё не загрузился.
  const photoCycle = useMemo(
    () => photoCycleFromExtras(surveyStatus?.extras, photosLast, photosByDate) ?? computeCycle(photosLast, 30, 7),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surveyStatus?.extras, photosLast, photosByDate],
  )

  const selectedDayWorkout = workoutByDate[selectedDateKey] ?? null
  // #619: черновик, оставшийся от уже сохранённой тренировки, — не «Продолжить».
  const draftStale = effectiveWorkoutDraft?.date === selectedDateKey && isWorkoutDraftStale(effectiveWorkoutDraft, selectedDayWorkout)
  useEffect(() => {
    if (draftStale && workoutDraft) {
      try { localStorage.removeItem('workout-draft') } catch { /* ignore */ }
    }
  }, [draftStale])
  const trainingStatus: 'idle' | 'active' | 'done' =
    effectiveWorkoutDraft?.date === selectedDateKey && !draftStale
      ? 'active'
      : selectedDayWorkout
        ? 'done'
        : 'idle'

  return (
    <Layout title="Дневник">
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes meas-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(255,149,0,0.55); }
          60%  { box-shadow: 0 0 0 8px rgba(255,149,0,0);    }
          100% { box-shadow: 0 0 0 0   rgba(255,149,0,0);    }
        }
        @keyframes report-breathe {
          0%, 100% { opacity: 0.7; transform: translateY(0); }
          50%      { opacity: 1;   transform: translateY(-1px); }
        }
        @keyframes periodic-card-glow {
          0%, 100% { box-shadow: 0 4px 14px rgba(74,108,247,0.18); }
          50%      { box-shadow: 0 6px 20px rgba(74,108,247,0.32); }
        }
        .periodic-card-active { animation: periodic-card-glow 2.6s ease-in-out infinite; }
        @keyframes periodic-card-urgent-glow {
          0%, 100% { box-shadow: 0 6px 18px rgba(255,59,48,0.32), 0 0 0 1px rgba(255,255,255,0.18) inset; }
          50%      { box-shadow: 0 10px 28px rgba(255,59,48,0.55), 0 0 0 1px rgba(255,255,255,0.28) inset; }
        }
        @keyframes periodic-urgent-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(255,255,255,0.55); }
          70%  { box-shadow: 0 0 0 12px rgba(255,255,255,0);    }
          100% { box-shadow: 0 0 0 0   rgba(255,255,255,0);    }
        }
        .periodic-card-urgent { animation: periodic-card-urgent-glow 1.6s ease-in-out infinite; }
        .report-tile-pending {
          animation: report-breathe 2.4s ease-in-out infinite;
        }
        .report-tile-pending:nth-child(2) { animation-delay: 0.3s; }
        .report-tile-pending:nth-child(3) { animation-delay: 0.6s; }
        .report-tile-pending:nth-child(4) { animation-delay: 0.9s; }
        .report-tile-pending:nth-child(5) { animation-delay: 1.2s; }
        .report-tile-pending:nth-child(6) { animation-delay: 1.5s; }
        .report-tile-pending:nth-child(7) { animation-delay: 1.8s; }
        .diary-day-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Day Strip / Calendar — sits directly on the page background */}
      <div style={{ fontFamily: F }}>
        <DayStrip
          selectedDate={selectedDate}
          onSelect={handleSelectDate}
          onToggleCalendar={handleToggleCalendar}
          calendarOpen={calendarOpen}
          calendarMonth={calendarMonth}
          onCalendarPrev={handleCalendarPrev}
          onCalendarNext={handleCalendarNext}
          diaryCache={diaryCache}
          measurementsByDate={measurementsByDate}
          photosByDate={photosByDate}
          workoutsByDate={workoutsByDate}
          unreadByDate={unreadByDate}
          onOpenComments={curatorHidden ? undefined : () => openInbox('NUTRITION')}
          unreadComments={curatorHidden ? 0 : unreadCommentsTotal}
        />
      </div>

      <div
        style={{
          padding: '8px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          fontFamily: F,
        }}
      >
        {/* Reports block (day/week toggle) — daily checks only */}
        {!selectedIsFuture && (
          <ReportsBlock
            diary={diary}
            diaryCache={diaryCache}
            strengthByDate={strengthByDate}
            cardioByDate={cardioByDate}
            lectureCountByDate={lectureCountByDate}
            selectedDate={selectedDate}
            workoutDraftDate={draftStale ? null : (effectiveWorkoutDraft?.date ?? null)}
            measWindowOpen={measCycle.state === 'open' || measCycle.state === 'overdue'}
            measWeekDone={measCycle.state === 'done'}
            photoWindowOpen={photoCycle.state === 'open' || photoCycle.state === 'overdue'}
            programDaysCount={programDaysCount}
            periodicEnabled={periodicReportTiles(isClub, showPhotoCard).meas}
            photosEnabled={periodicReportTiles(isClub, showPhotoCard).photo}
            metricsEnabled={featureAccess.has('metrics')}
            gamificationOn={featureAccess.has('gamification')}
            techniqueByDate={techniqueByDate}
            techniqueEarnedByDate={techniqueEarnedByDate}
            xpEarnedByDate={xpEarnedByDate}
            onOpenMetrics={guardDemo(() => { setMetricsFocus(undefined); setShowMetricsSheet(true) })}
            onOpenNutrition={guardDemo(() => {
              // «+Питание» из Отчётов = стандартное ручное дневное КБЖУ — единообразно
              // для всех, в т.ч. при подключённом ФС (синк перезапишет день только
              // если у ФС есть данные за этот день; иначе ручные цифры сохраняются).
              setShowDayNutritionSheet(true)
            })}
            onOpenTraining={handleTrainingNavigate}
            onOpenLectures={() => navigate('/lectures')}
            measurementsByDate={measurementsByDate}
            photosByDate={photosByDate}
            onOpenMeasurements={handleOpenMeasurements}
            onOpenPhotos={
              photoCycle.state === 'open' || photoCycle.state === 'overdue'
                ? guardDemo(() => setShowPhotoReportSheet(true))
                : () => navigate('/progress-photos')
            }
            onOpenAnalytics={() => { selectionHaptic(); navigate('/analytics') }}
            hasUnreadMetricsComment={!curatorHidden && hasUnreadMetricsComment}
            hasMetricsComment={!curatorHidden && !!metricsComment}
            onOpenMetricsComment={curatorHidden ? undefined : () => { selectionHaptic(); openInbox('NUTRITION') }}
          />
        )}

        {/* Periodic reports — Замеры/Фото — статично наверху под отчётами,
            вне зависимости от тумблера день/неделя и состояния (open/done/closed/overdue).
            #535: в Клубе фото прогресса не сдают — их никто не проверяет; замеры
            остаются, человек делает их для себя, и считаются они по календарной
            неделе, а не по слотам программы. */}
        {(showMeasCard || showPhotoCard) && !selectedIsFuture && (measurementsLoading || (showPhotoCard && photosLoading)) && (
          <SkeletonRow cols={showMeasCard && showPhotoCard ? 2 : 1} height={78} />
        )}
        {(showMeasCard || showPhotoCard) && !selectedIsFuture && !measurementsLoading && (!showPhotoCard || !photosLoading) && (
          <div style={{ display: 'grid', gridTemplateColumns: showMeasCard && showPhotoCard ? '1fr 1fr' : '1fr', gap: 8 }}>
            {showMeasCard && (
            <PeriodicReportCard
              testId="measurements-card"
              title="Замеры тела"
              Icon={Scale}
              color="#4A6CF7"
              state={measCycle.state}
              xp={10}
              earnedXp={measCycle.state === 'done' ? 10 : undefined}
              lastSubmittedAt={measCycle.lastSubmittedAt}
              nextOpenAt={measCycle.nextOpenAt}
              windowEndAt={measCycle.windowEndAt}
              // Сдача доступна только когда окно открыто (open/overdue).
              // В состояниях done/closed карточка не открывает форму — нужно дождаться следующего открытия.
              onClick={
                measCycle.state === 'open' || measCycle.state === 'overdue'
                  ? handleOpenMeasurementsForWindow
                  : undefined
              }
            />
            )}
            {showPhotoCard && (
            <PeriodicReportCard
              title="Фото прогресса"
              Icon={Camera}
              color="#4A6CF7"
              state={photoCycle.state}
              xp={10}
              earnedXp={photoCycle.state === 'done' ? 10 : undefined}
              lastSubmittedAt={photoCycle.lastSubmittedAt}
              nextOpenAt={photoCycle.nextOpenAt}
              windowEndAt={photoCycle.windowEndAt}
              missedWindowEndAt={photoCycle.missedWindowEndAt}
              onClick={
                photoCycle.state === 'done' || photoCycle.state === 'closed'
                  ? () => navigate('/progress-photos')
                  : guardDemo(() => setShowPhotoReportSheet(true))
              }
            />
            )}
          </div>
        )}

        {/* Допы: анкеты обратной связи / NPS — показываем когда окно открыто или уже получено */}
        {!selectedIsFuture && !isClub && openSurveyExtras.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {openSurveyExtras.map((e) => (
              <ExtraSurveyCard
                key={e.slot}
                title={e.label}
                Icon={e.kind === 'NPS' ? Star : MessageSquareHeart}
                color={e.kind === 'NPS' ? '#F59E0B' : '#7C3AED'}
                claimed={e.claimed}
                tucoin={e.tucoin}
                onClick={guardDemo(() => (e.kind === 'NPS' ? setShowNpsSheet(true) : setShowFeedbackSheet(true)))}
                onDismiss={e.claimed ? () => dismissSurvey(e.slot) : undefined}
              />
            ))}
          </div>
        )}

        {/* Date context banner (when not today) */}
        <DateContextBanner selectedDate={selectedDate} onGoToToday={handleGoToToday} />

        {/* Future placeholder */}
        {selectedIsFuture && <FuturePlaceholder plannedType={plannedTypeForSelected} />}

        {/* Loading skeleton */}
        {diaryLoading && !selectedIsFuture && <DiarySkeleton />}

        {/* Main diary content */}
        {!diaryLoading && !selectedIsFuture && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            {/* Compact nutrition widget — single block, expandable to FS list or manual entry */}
            <CompactNutritionWidget
              diary={diary}
              meals={meals}
              goal={nutritionGoal}
              // #546: без синхронизации на тарифе подсказка «Подключи FatSecret» — ложь.
              fatSecretConnected={fatSecretConnected || fatSecretState !== 'open'}
              fatSecretState={fatSecretState}
              onOpenMeal={handleAddMeal}
              onOpenManual={guardDemo(() => setShowDayNutritionSheet(true))}
              onConnectFS={() => setFsSheet({ open: true, mode: 'connect' })}
              onSync={() => { selectionHaptic(); syncFatSecretMutation.mutate() }}
              isSyncing={syncFatSecretMutation.isPending}
              hasUnreadComment={!curatorHidden && hasUnreadNutritionComment}
              onOpenCommentsHistory={curatorHidden ? undefined : () => openInbox('NUTRITION')}
              onEditGoal={goalSelfManaged ? () => { selectionHaptic(); setGoalSheetOpen(true) } : undefined}
            />

            {/* #306: клиент сам оставляет комментарий по питанию за день —
                куратор ответит в своём чате. С комментариями тред открыт сразу.
                #534: в Клубе отвечать некому — тред не показываем. */}
            {diary?.id && !curatorHidden && (() => {
              const dayComments = (diary.comments ?? []).filter(
                (c: CuratorCommentDto) => (c.targetType ?? 'DIARY_ENTRY') === 'DIARY_ENTRY',
              )
              if (dayComments.length > 0 || nutritionThreadOpen) {
                return (
                  <div style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(26,58,92,0.08)', borderRadius: 14, padding: '10px 12px' }}>
                    <CuratorCommentThread
                      targetType="DIARY_ENTRY"
                      targetId={diary.id}
                      comments={dayComments}
                      invalidateKeys={[['diary'], ['diary', 'all-comments']]}
                      title="Комментарии по питанию за день"
                      compact
                    />
                  </div>
                )
              }
              return (
                <button
                  data-testid="nutrition-day-comment-open"
                  onClick={() => setNutritionThreadOpen(true)}
                  style={{
                    alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'transparent', border: 'none', padding: '0 4px', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, color: '#6C5CE7', fontFamily: F,
                  }}
                >
                  💬 Комментарий по питанию за день
                </button>
              )
            })()}

            {/* Активный челлендж — над тренировкой; тап → шторка с описанием и чатом */}
            <ActiveChallengeBlock variant="diary" />

            {/* Training card — полноценная для всех продуктов, включая Клуб
                (Клуб сам выбирает программу из шаблона на экране /diary/workout). */}
            <div
              ref={trainingCardRef}
              style={{
                borderRadius: 22,
                transition: 'box-shadow 0.35s ease',
                boxShadow: trainingHighlight
                  ? '0 0 0 3px rgba(74,108,247,0.55), 0 10px 28px rgba(74,108,247,0.25)'
                  : 'none',
              }}
            >
              <TrainingCard
                diary={diary}
                status={trainingStatus}
                videoTechniqueDone={techniqueByDate.has(selectedDateKey)}
                startedAt={trainingStatus === 'active' ? effectiveWorkoutDraft?.startedAt : undefined}
                workoutEntry={selectedDayWorkout}
                aggregates={{ ...trainingAggregates, streak: streakDays }}
                onNavigate={handleTrainingNavigate}
                onEdit={() => {
                  selectionHaptic()
                  if (selectedDayWorkout?.id) {
                    navigate(`/diary/workout/active?entryId=${selectedDayWorkout.id}`)
                  } else {
                    navigate('/diary/workout/active')
                  }
                }}
                onOpenWorkouts={() => {
                  selectionHaptic()
                  navigate(`/diary/workout?date=${selectedDateKey}`)
                }}
                todayExercises={todayExercises}
                dayNumber={todayProgramDay?.dayNumber}
                unreadComments={curatorHidden ? 0 : unreadByType.WORKOUT_EXERCISE + unreadByType.WORKOUT_ENTRY}
                isCuratorViewing={isCuratorViewing}
                isAdminView={isAdminView}
                hasProgram={hasProgram}
                onEditProgram={() => { selectionHaptic(); navigate('/workout') }}
              />
            </div>

            {/* SOS — кнопка экстренной поддержки от ИИ Павла. Под тренировкой.
                #568: только там, где ИИ по тарифу открыт. */}
            {!selectedIsFuture && aiOpen && (
              <button
                data-testid="diary-sos-btn"
                onClick={() => { selectionHaptic(); setShowSosSheet(true) }}
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: 16,
                  padding: '14px 16px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #FF3B30 0%, #E12B22 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontFamily: F,
                  textAlign: 'left',
                  boxShadow: '0 8px 22px rgba(255,59,48,0.32), 0 0 0 1px rgba(255,255,255,0.18) inset',
                }}
              >
                {/* Circle with SOS and centered pulse-ring */}
                <div style={{
                  position: 'relative',
                  width: 40, height: 40, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1,
                }}>
                  {/* Pulse ring — concentric with the circle */}
                  <span style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    animation: 'sos-pulse 1.8s ease-out infinite',
                    pointerEvents: 'none',
                  }} />
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.22)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid rgba(255,255,255,0.45)',
                    position: 'relative', zIndex: 1,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: 0.5, lineHeight: 1 }}>SOS</span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>Хочу сорваться</div>
                  <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.3, marginTop: 2 }}>
                    Напиши Павлу — поможет справиться с желанием
                  </div>
                </div>
                <span style={{
                  fontSize: 22, lineHeight: 1, position: 'relative', zIndex: 1,
                  opacity: 0.85, flexShrink: 0,
                }}>›</span>
                <style>{`
                  @keyframes sos-pulse {
                    0%   { box-shadow: 0 0 0 0   rgba(255,255,255,0.55); }
                    70%  { box-shadow: 0 0 0 14px rgba(255,255,255,0);    }
                    100% { box-shadow: 0 0 0 0   rgba(255,255,255,0);    }
                  }
                `}</style>
              </button>
            )}

            {/* Notes */}
            {diary?.notes && (
              <GlassCard style={{ padding: 14, borderRadius: 16 }}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    fontFamily: F,
                    marginBottom: 6,
                    display: 'block',
                  }}
                >
                  Заметки
                </span>
                <p
                  style={{
                    fontSize: 13,
                    color: C.secondary,
                    fontFamily: F,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {diary.notes}
                </p>
              </GlassCard>
            )}

            {/* Photo with data */}
            {diary?.photoUrl && (
              <GlassCard style={{ padding: 0, borderRadius: 18, overflow: 'hidden' }}>
                <img
                  src={diary.photoUrl}
                  alt="Progress"
                  style={{ width: '100%', height: 180, objectFit: 'cover' }}
                />
                <div style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: F }}>
                    Фото прогресса
                  </span>
                </div>
              </GlassCard>
            )}

            {/* Analytics mini-card */}
            <AnalyticsMiniCard
              diaryCache={diaryCache}
              measurementByDate={measurementByDate}
              onOpenDetail={() => { selectionHaptic(); navigate('/analytics') }}
            />
          </motion.div>
        )}
      </div>

      <NutritionGoalSheet open={goalSheetOpen} onClose={() => setGoalSheetOpen(false)} />

      <MetricsEditSheet
        open={showMetricsSheet}
        onClose={() => { setShowMetricsSheet(false); setMetricsFocus(undefined) }}
        initialValues={metricsValues}
        onSave={(v) => {
          setMetricsValues(v)
          saveMetricsMutation.mutate(v)
        }}
        saving={saveMetricsMutation.isPending}
        focusField={metricsFocus}
        stepsGoal={stepsGoalData?.stepsGoal ?? null}
        submittedToday={{
          // #289: «сдано» строго по записи выбранного дня — в новый день пусто,
          // поля свободны, прошлые значения показываются серой маской (initialValues).
          sleep: diary?.sleepQuality != null || (diary?.sleep != null),
          steps: diary?.steps != null,
          weight: diary?.weight != null,
        }}
      />
      <FeedbackSurveySheet open={showFeedbackSheet} onClose={() => setShowFeedbackSheet(false)} curator={(dashboardData as { curator?: { name?: string | null; avatarUrl?: string | null } } | undefined)?.curator ?? null} />
      <NpsSurveySheet open={showNpsSheet} onClose={() => setShowNpsSheet(false)} curator={(dashboardData as { curator?: { name?: string | null; avatarUrl?: string | null } } | undefined)?.curator ?? null} />
      <MeasurementsSheet
        open={showMeasurementsSheet}
        onClose={() => setShowMeasurementsSheet(false)}
        onSubmitted={(streak, opts) => {
          if (opts?.isEdit) {
            // Редактирование уже сданных замеров в открытом окне — без повторного
            // праздника/«награды», только тихое подтверждение.
            toast.success('Изменения замеров сохранены')
            celebrateStreakThen(streak)
          } else {
            setMeasurementsXpAwarded(opts?.xpAwarded ?? null)
            celebrateStreakThen(streak, () => setShowMeasurementsCongrats(true))
          }
        }}
        date={selectedDateKey}
        existing={measurementByDate[selectedDateKey] ?? null}
        lastValues={lastMeasurementValues}
        weeklyLockedAt={weeklyMeasurementKey}
        onOpenLockedDay={(d) => setSelectedDate(new Date(`${d}T00:00:00`))}
        isClub={isClub}
      />
      <MeasurementsCongrats
        open={showMeasurementsCongrats}
        onClose={() => setShowMeasurementsCongrats(false)}
        pastDay={isPastDate(selectedDate)}
        xpAwarded={measurementsXpAwarded}
      />
      <StreakSuccessModal
        open={streakCelebration.open}
        streakDays={streakCelebration.days}
        bonusTucoin={streakCelebration.bonusTucoin}
        onClose={closeStreakCelebration}
      />
      <PhotoReportSheet
        open={showPhotoReportSheet}
        onClose={() => setShowPhotoReportSheet(false)}
        botUsername={(dashboardData as { botUsername?: string | null } | undefined)?.botUsername ?? null}
      />
      <AnimatePresence>
        {showSosSheet && aiOpen && (
          <PavelChatSheet
            onClose={() => setShowSosSheet(false)}
            topicLabel="🆘 SOS · Хочу сорваться"
            seedAssistantMessage={
              'Эй, держись! Я рядом — расскажи, что происходит?\n\n' +
              '• Что именно тянет сорваться сейчас — голод, стресс, эмоции, скука, усталость?\n' +
              '• Когда это началось — после конкретного триггера или накапливалось?\n' +
              '• Сколько по 10-балльной шкале сейчас желание сорваться?\n\n' +
              'Разберём по шагам и придумаем как пройти этот момент. Если поймём, что нужно глубже — подскажу когда стоит обратиться к куратору.'
            }
            suggestedReplies={[
              'Хочу сладкое',
              'Стресс на работе',
              'Скучно',
              'Не хочу тренироваться',
              'Сорвался(лась) уже — что делать?',
            ]}
          />
        )}
      </AnimatePresence>
      {/* #266/#270: единый инбокс комментариев куратора (табы Всё/Питание/Тренировки).
          Старые окна (питание/показатели) заменены им — см. openInbox. */}
      <CommentsInboxSheet
        open={inbox.open}
        onClose={() => setInbox((s) => ({ ...s, open: false }))}
        comments={allComments}
        initialTab={inbox.tab}
        onReply={commentsInbox.reply}
        perspective={commentsInbox.perspective}
      />
      <RewardCongrats
        open={metricsCongrats.open}
        onClose={() => setMetricsCongrats({ open: false, xp: 0 })}
        icon={SneakerIcon}
        title="Показатели сохранены!"
        subtitle="Отслеживай метрики каждый день — это держит фокус на прогрессе."
        xp={metricsCongrats.xp}
        note={metricsCongrats.note}
        gradient="linear-gradient(135deg, #4A9FE5 0%, #6C5CE7 100%)"
        testId="modal-metrics-congrats"
      />
      <AddMealSheet
        open={showAddMealSheet}
        onClose={() => setShowAddMealSheet(false)}
        mealType={addMealType}
        diaryEntryId={diary?.id || null}
        date={selectedDateKey}
        meal={meals.find((m) => m.mealType === addMealType) ?? null}
        onStreak={(streak) => celebrateStreakThen(streak)}
      />
      <DayNutritionSheet
        open={showDayNutritionSheet}
        onClose={() => setShowDayNutritionSheet(false)}
        date={selectedDateKey}
        initialValues={{
          calories: diary?.calories != null ? String(diary.calories) : '',
          protein: diary?.protein != null ? String(diary.protein) : '',
          fat: diary?.fat != null ? String(diary.fat) : '',
          carbs: diary?.carbs != null ? String(diary.carbs) : '',
          fiber: diary?.fiber != null ? String(diary.fiber) : '',
        }}
        enabledMetrics={enabledMetrics}
        metricsInitial={(diary as { metrics?: Record<string, number> } | null)?.metrics ?? {}}
        onEnabledChange={() => queryClient.invalidateQueries({ queryKey: ['nutrition-metrics'] })}
        // #569: подсказку «Подключи FatSecret» — только если тариф её даёт.
        fatSecretConnected={!canOfferFatSecretConnect(fatSecretConnected, fatSecretState)}
      />
      <FatSecretConnectSheet
        open={fsSheet.open}
        onClose={() => setFsSheet({ ...fsSheet, open: false })}
        onConnect={() => connectFatSecretMutation.mutate()}
        isPending={connectFatSecretMutation.isPending}
        mode={fsSheet.mode}
      />
    </Layout>
  )
}
